/**
 * Build a channel-namespaced, Firestore-safe turn key from a transport's provider
 * message id (§12.1). Used verbatim as the turn-ledger document id, so it must
 * never contain '/' — provider ids (e.g. a WhatsApp wamid) can, so we replace it.
 * '_' never occurs in the base64 wamid / Twilio SID alphabets, so this is
 * collision-free.
 */
export function makeTurnKey(channel, providerId) {
    return `${channel}:${providerId.replace(/\//g, '_')}`;
}
/** The decision branch that makes a job offer. */
export const OFFER_DECISION_BRANCH = 'offer';
/** Current instant as a UTC epoch-millisecond value. Use instead of `Date.now()`. */
export function nowUtc() {
    return Date.now();
}
/** Brand a raw epoch-millisecond number (e.g. from `date.getTime()`) as `UtcMs`. */
export function toUtcMs(epochMillis) {
    return epochMillis;
}
/** Machine-readable UTC ISO-8601 string, e.g. "2026-05-01T17:00:00.000Z". Zone-independent. */
export function toUtcIso(instant) {
    return new Date(instant).toISOString();
}
// Rendering: every helper REQUIRES a timezone and there are no optional args.
// Each variant bakes in the full Intl.DateTimeFormatOptions it needs, so callers
// choose a helper by intent instead of passing (and forgetting) format options.
/** "May 1, 2026" — calendar date in the given zone. */
export function renderDateUtc(instant, timezone) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(new Date(instant));
}
/** "10:00 AM PDT" — time-of-day with zone abbreviation in the given zone. */
export function renderTimeUtc(instant, timezone) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(new Date(instant));
}
/** "May 1, 2026, 10:00 AM PDT" — full date and time in the given zone. */
export function renderDateTimeUtc(instant, timezone) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(new Date(instant));
}
/** "Monday, May 1" — weekday and date (no year) in the given zone. */
export function renderWeekdayDateUtc(instant, timezone) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    }).format(new Date(instant));
}
/** "Mon, May 1, 10:00 AM PDT" — compact weekday + date + time for slot lists. */
export function renderWeekdayDateTimeUtc(instant, timezone) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(new Date(instant));
}
/**
 * Resolve a recruiter-local wall-clock date + hour (in the given IANA zone) to
 * the absolute UTC instant it denotes. Handles DST and fractional-hour offsets
 * (e.g. India +5:30). Inverse of `utcMsToLocalHour`.
 */
export function localWallClockToUtcMs(dateLocal, hourLocal, timezone) {
    const [year, month, day] = dateLocal.split('-').map(Number);
    // Rough UTC guess for the wall-clock, then correct by the offset Intl reports
    // for that instant in the target zone.
    const rough = new Date(Date.UTC(year, month - 1, day, hourLocal, 0, 0, 0));
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(rough);
    const pick = (t) => Number(parts.find((p) => p.type === t)?.value ?? '0');
    const rawHour = pick('hour');
    const localHour = rawHour === 24 ? 0 : rawHour;
    const diffMinutes = hourLocal * 60 - (localHour * 60 + pick('minute'));
    const wrapped = (diffMinutes + 1440) % 1440;
    const finalDiff = wrapped > 720 ? wrapped - 1440 : wrapped;
    return toUtcMs(rough.getTime() + finalDiff * 60000);
}
/**
 * Express a UTC instant as recruiter-local wall-clock parts (date + top-of-hour)
 * in the given zone — used to seed wall-clock pickers/grids from a stored instant.
 * Inverse of `localWallClockToUtcMs`.
 */
export function utcMsToLocalHour(instant, timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', hour12: false,
    }).formatToParts(new Date(instant));
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    const hour = get('hour') === '24' ? 0 : Number(get('hour'));
    return { dateLocal: `${get('year')}-${get('month')}-${get('day')}`, hourLocal: hour };
}
/** Absolute end instant of a slot (`startTimeUtc + durationMinutes`). */
export function slotEndUtc(slot) {
    return toUtcMs(slot.startTimeUtc + slot.durationMinutes * 60000);
}
export { isPhoneE164, toPhoneE164, maskPhoneE164, InvalidPhoneNumberError } from './phoneE164';
// API Error Codes - shared between client and server
export const ErrorCodes = {
    // Authentication & Authorization
    ADMIN_REQUIRED: 'ADMIN_REQUIRED',
    USER_NOT_AUTHORIZED: 'USER_NOT_AUTHORIZED',
    EMAIL_REQUIRED: 'EMAIL_REQUIRED',
    USER_CREATION_ERROR: 'USER_CREATION_ERROR',
    USER_LOGIN_ERROR: 'USER_LOGIN_ERROR',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    USER_UPDATE_ERROR: 'USER_UPDATE_ERROR',
    USER_DELETE_ERROR: 'USER_DELETE_ERROR',
    USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
    USER_ALREADY_REGISTERED: 'USER_ALREADY_REGISTERED',
    PERMISSION_CHECK_ERROR: 'PERMISSION_CHECK_ERROR',
    NO_TOKEN: 'NO_TOKEN',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_REVOKED: 'TOKEN_REVOKED',
    INVALID_TOKEN: 'INVALID_TOKEN',
    AUTH_SERVICE_ERROR: 'AUTH_SERVICE_ERROR',
    AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
    // Job-related errors
    INVALID_JOB_DESCRIPTION: 'INVALID_JOB_DESCRIPTION',
    JOB_DESCRIPTION_PARSING_ERROR: 'JOB_DESCRIPTION_PARSING_ERROR',
    JOB_CREATION_ERROR: 'JOB_CREATION_ERROR',
    JOB_PARSING_ERROR: 'JOB_PARSING_ERROR',
    JOB_FETCH_ERROR: 'JOB_FETCH_ERROR',
    ORGS_FETCH_ERROR: 'ORGS_FETCH_ERROR',
    JOB_UPDATE_ERROR: 'JOB_UPDATE_ERROR',
    JOB_UPDATE_FORBIDDEN: 'JOB_UPDATE_FORBIDDEN',
    JOB_DELETION_ERROR: 'JOB_DELETION_ERROR',
    JOB_DELETE_FORBIDDEN: 'JOB_DELETE_FORBIDDEN',
    JOB_OPTIMIZATION_ERROR: 'JOB_OPTIMIZATION_ERROR',
    OPTIMIZATION_QUESTIONS_ERROR: 'OPTIMIZATION_QUESTIONS_ERROR',
    AI_PARSING_FAILED: 'AI_PARSING_FAILED',
    JOB_NOT_FOUND: 'JOB_NOT_FOUND',
    // Candidate-related errors
    CANDIDATE_NOT_FOUND: 'CANDIDATE_NOT_FOUND',
    CANDIDATE_JOB_NOT_FOUND: 'CANDIDATE_JOB_NOT_FOUND',
    CANDIDATE_FETCH_ERROR: 'CANDIDATE_FETCH_ERROR',
    CANDIDATE_CREATION_ERROR: 'CANDIDATE_CREATION_ERROR',
    CANDIDATES_FETCH_ERROR: 'CANDIDATES_FETCH_ERROR',
    CANDIDATE_DELETE_FORBIDDEN: 'CANDIDATE_DELETE_FORBIDDEN',
    CANDIDATE_ACCESS_FORBIDDEN: 'CANDIDATE_ACCESS_FORBIDDEN',
    CANDIDATE_DELETE_ERROR: 'CANDIDATE_DELETE_ERROR',
    CANDIDATE_SCORE_ERROR: 'CANDIDATE_SCORE_ERROR',
    CANDIDATE_UPDATE_FORBIDDEN: 'CANDIDATE_UPDATE_FORBIDDEN',
    CANDIDATE_NOT_ASSOCIATED_WITH_JOB: 'CANDIDATE_NOT_ASSOCIATED_WITH_JOB',
    CANDIDATE_ALREADY_ASSOCIATED: 'CANDIDATE_ALREADY_ASSOCIATED',
    /** Same person already has an application for the target job. */
    CANDIDATE_ALREADY_APPLIED: 'CANDIDATE_ALREADY_APPLIED',
    INVALID_SMS_CONSENT: 'INVALID_SMS_CONSENT',
    INVALID_CANDIDATE_COMMUNICATIONS: 'INVALID_CANDIDATE_COMMUNICATIONS',
    NO_ACTIVE_RESPONSE_FOLLOW_UP: 'NO_ACTIVE_RESPONSE_FOLLOW_UP',
    RESPONSE_FOLLOW_UP_ERROR: 'RESPONSE_FOLLOW_UP_ERROR',
    APPLICATION_ACKNOWLEDGEMENT_NOT_RETRYABLE: 'APPLICATION_ACKNOWLEDGEMENT_NOT_RETRYABLE',
    APPLICATION_ACKNOWLEDGEMENT_ERROR: 'APPLICATION_ACKNOWLEDGEMENT_ERROR',
    CANDIDATE_AUTOMATION_ERROR: 'CANDIDATE_AUTOMATION_ERROR',
    INVALID_QUALIFICATION_WORKFLOW: 'INVALID_QUALIFICATION_WORKFLOW',
    /** No channel can carry a message to this candidate (no number, opted out, no consent…). */
    CANDIDATE_UNREACHABLE: 'CANDIDATE_UNREACHABLE',
    /** Email/phone partial match — needs user resolution before merge. */
    CANDIDATE_CONFLICT: 'CANDIDATE_CONFLICT',
    CANDIDATE_JOB_ASSOCIATION_ERROR: 'CANDIDATE_JOB_ASSOCIATION_ERROR',
    SCORE_UPDATE_ERROR: 'SCORE_UPDATE_ERROR',
    HIRING_STATUS_UPDATE_ERROR: 'HIRING_STATUS_UPDATE_ERROR',
    ACCEPT_CANDIDATE_ERROR: 'ACCEPT_CANDIDATE_ERROR',
    // Resume parsing errors
    NO_PDF_DATA: 'NO_PDF_DATA',
    PDF_PARSE_ERROR: 'PDF_PARSE_ERROR',
    PDF_PARSING_FAILED: 'PDF_PARSING_FAILED',
    NO_DOC_DATA: 'NO_DOC_DATA',
    DOC_PARSE_ERROR: 'DOC_PARSE_ERROR',
    DOC_PARSING_FAILED: 'DOC_PARSING_FAILED',
    NO_FILES: 'NO_FILES',
    BATCH_PARSE_ERROR: 'BATCH_PARSE_ERROR',
    PARSE_ERROR: 'PARSE_ERROR',
    PARSING_ERROR: 'PARSING_ERROR',
    TEXT_PARSING_FAILED: 'TEXT_PARSING_FAILED',
    URL_PARSING_FAILED: 'URL_PARSING_FAILED',
    URL_FETCH_FAILED: 'URL_FETCH_FAILED',
    RESUME_PARSE_FAILED: 'RESUME_PARSE_FAILED',
    MISSING_INPUT: 'MISSING_INPUT',
    // Validation errors
    INVALID_MANUAL_PAYLOAD: 'INVALID_MANUAL_PAYLOAD',
    INVALID_PAYLOAD_TYPE: 'INVALID_PAYLOAD_TYPE',
    INVALID_SOURCE_TYPE: 'INVALID_SOURCE_TYPE',
    INVALID_PARSE_PAYLOAD: 'INVALID_PARSE_PAYLOAD',
    INVALID_MULTIPART: 'INVALID_MULTIPART',
    INVALID_REQUEST: 'INVALID_REQUEST',
    INVALID_TEXT_PAYLOAD: 'INVALID_TEXT_PAYLOAD',
    INVALID_PDF_PAYLOAD: 'INVALID_PDF_PAYLOAD',
    INVALID_DOC_PAYLOAD: 'INVALID_DOC_PAYLOAD',
    INVALID_URL_PAYLOAD: 'INVALID_URL_PAYLOAD',
    INVALID_STATE: 'INVALID_STATE',
    INVALID_UPDATES: 'INVALID_UPDATES',
    INVALID_PIPELINE_TEMPLATE: 'INVALID_PIPELINE_TEMPLATE',
    INVALID_CONFIRMATION: 'INVALID_CONFIRMATION',
    UNSUPPORTED_CONTENT_TYPE: 'UNSUPPORTED_CONTENT_TYPE',
    UNSUPPORTED_SOURCE_TYPE: 'UNSUPPORTED_SOURCE_TYPE',
    STREAM_ERROR: 'STREAM_ERROR',
    INVALID_SCORE: 'INVALID_SCORE',
    INVALID_STATUS: 'INVALID_STATUS',
    MISSING_IDS: 'MISSING_IDS',
    // Interview & Questions
    MISSING_JOB_ID: 'MISSING_JOB_ID',
    AI_QUESTIONS_DISABLED: 'AI_QUESTIONS_DISABLED',
    QUALIFICATION_QUESTIONS_ERROR: 'QUALIFICATION_QUESTIONS_ERROR',
    QUESTIONS_STATUS_ERROR: 'QUESTIONS_STATUS_ERROR',
    QUESTION_ALREADY_SENT: 'QUESTION_ALREADY_SENT',
    INTERVIEW_INVITATION_ERROR: 'INTERVIEW_INVITATION_ERROR',
    INTERVIEW_CANCELLATION_ERROR: 'INTERVIEW_CANCELLATION_ERROR',
    NO_INVITATION: 'NO_INVITATION',
    RESCHEDULE_REQUEST_ERROR: 'RESCHEDULE_REQUEST_ERROR',
    RESCHEDULE_ERROR: 'RESCHEDULE_ERROR',
    MISSING_FIELDS: 'MISSING_FIELDS',
    // Messaging
    MESSAGE_SENDING_ERROR: 'MESSAGE_SENDING_ERROR',
    MESSAGE_FETCH_ERROR: 'MESSAGE_FETCH_ERROR',
    CHAT_ID_REQUIRED: 'CHAT_ID_REQUIRED',
    CHAT_NOT_FOUND: 'CHAT_NOT_FOUND',
    CHAT_ACCESS_FORBIDDEN: 'CHAT_ACCESS_FORBIDDEN',
    MESSAGE_DELIVERY_ERROR: 'MESSAGE_DELIVERY_ERROR',
    // State & Data
    MISSING_STATE: 'MISSING_STATE',
    STATE_UPDATE_ERROR: 'STATE_UPDATE_ERROR',
    DATA_SUMMARY_ERROR: 'DATA_SUMMARY_ERROR',
    DATA_DELETION_ERROR: 'DATA_DELETION_ERROR',
    // Allowed Users
    ALLOWED_USER_ADD_ERROR: 'ALLOWED_USER_ADD_ERROR',
    ALLOWED_USERS_FETCH_ERROR: 'ALLOWED_USERS_FETCH_ERROR',
    ALLOWED_USER_REMOVE_ERROR: 'ALLOWED_USER_REMOVE_ERROR',
    // Admin & Users
    USERS_FETCH_ERROR: 'USERS_FETCH_ERROR',
    UPDATE_USER_ERROR: 'UPDATE_USER_ERROR',
    GET_USER_DETAILS_ERROR: 'GET_USER_DETAILS_ERROR',
    USER_SNAPSHOT_ERROR: 'USER_SNAPSHOT_ERROR',
    CHATS_FETCH_ERROR: 'CHATS_FETCH_ERROR',
    WAITLIST_FETCH_ERROR: 'WAITLIST_FETCH_ERROR',
    // Settings
    SETTINGS_FETCH_ERROR: 'SETTINGS_FETCH_ERROR',
    SETTINGS_UPDATE_REQUIRED: 'SETTINGS_UPDATE_REQUIRED',
    SETTINGS_UPDATE_ERROR: 'SETTINGS_UPDATE_ERROR',
    // Feedback
    FEEDBACK_SUBMISSION_ERROR: 'FEEDBACK_SUBMISSION_ERROR',
    // Pending Session (Value-First Onboarding)
    SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
    PENDING_SESSION_ERROR: 'PENDING_SESSION_ERROR',
    RESUME_UPLOAD_ERROR: 'RESUME_UPLOAD_ERROR',
    JOB_DESCRIPTION_ERROR: 'JOB_DESCRIPTION_ERROR',
    CONVERSION_ERROR: 'CONVERSION_ERROR',
    MAX_RESUMES_EXCEEDED: 'MAX_RESUMES_EXCEEDED',
    // Talent Pool (Phase 0 sourcing)
    TALENT_POOL_ERROR: 'TALENT_POOL_ERROR',
    // Public Job Application
    PERMISSION_DENIED: 'PERMISSION_DENIED',
    UPDATE_FAILED: 'UPDATE_FAILED',
    MISSING_REQUIRED_FIELDS: 'MISSING_REQUIRED_FIELDS',
    APPLICATION_ERROR: 'APPLICATION_ERROR',
    STATUS_CHECK_ERROR: 'STATUS_CHECK_ERROR',
    ALREADY_APPLIED: 'ALREADY_APPLIED',
    INVALID_EMAIL: 'INVALID_EMAIL',
    INVALID_JOB_ID: 'INVALID_JOB_ID',
    INVALID_FORM_ANSWERS: 'INVALID_FORM_ANSWERS',
    INVALID_SESSION_ID: 'INVALID_SESSION_ID',
    // System
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    /** Requested Twilio Content template SID does not exist / isn't visible. */
    WHATSAPP_TEMPLATE_NOT_FOUND: 'WHATSAPP_TEMPLATE_NOT_FOUND',
    // Organization
    ORG_NOT_FOUND: 'ORG_NOT_FOUND',
    ORG_ALREADY_EXISTS: 'ORG_ALREADY_EXISTS',
    ORG_PERMISSION_DENIED: 'ORG_PERMISSION_DENIED',
    ORG_MEMBER_ALREADY_EXISTS: 'ORG_MEMBER_ALREADY_EXISTS',
    ORG_MEMBER_NOT_FOUND: 'ORG_MEMBER_NOT_FOUND',
    ORG_OWNER_CANNOT_LEAVE: 'ORG_OWNER_CANNOT_LEAVE',
    ORG_INVITE_ERROR: 'ORG_INVITE_ERROR',
    ORG_SUBSCRIPTION_UPDATE_ERROR: 'ORG_SUBSCRIPTION_UPDATE_ERROR',
    ORG_INVITE_NOT_FOUND: 'ORG_INVITE_NOT_FOUND',
    ORG_INVITE_EXPIRED: 'ORG_INVITE_EXPIRED',
    ORG_USER_ALREADY_IN_ORG: 'ORG_USER_ALREADY_IN_ORG',
    ORG_TRANSFER_ERROR: 'ORG_TRANSFER_ERROR',
    ORG_MOVE_USER_ERROR: 'ORG_MOVE_USER_ERROR',
    ORG_OWNER_CANNOT_BE_MOVED: 'ORG_OWNER_CANNOT_BE_MOVED',
    // Phone verification (Free-tier SMS gating)
    INVALID_PHONE_NUMBER: 'INVALID_PHONE_NUMBER',
    PHONE_VERIFICATION_REQUIRED: 'PHONE_VERIFICATION_REQUIRED',
    PHONE_VERIFICATION_FAILED: 'PHONE_VERIFICATION_FAILED',
    PHONE_ALREADY_LINKED: 'PHONE_ALREADY_LINKED',
    PHONE_CHANGE_COOLDOWN: 'PHONE_CHANGE_COOLDOWN',
    PHONE_VERIFY_RATE_LIMIT: 'PHONE_VERIFY_RATE_LIMIT',
    BULK_SEND_REQUIRES_UPGRADE: 'BULK_SEND_REQUIRES_UPGRADE',
    SMS_CONSENT_REQUIRED: 'SMS_CONSENT_REQUIRED',
    CONSENT_GRANT_ERROR: 'CONSENT_GRANT_ERROR',
    INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
    // Plan-limit gating (subscription-tier caps on stored entities)
    JOB_LIMIT_REACHED: 'JOB_LIMIT_REACHED',
    CANDIDATE_LIMIT_REACHED: 'CANDIDATE_LIMIT_REACHED',
    TEAM_LIMIT_REACHED: 'TEAM_LIMIT_REACHED',
    // Anti-ghosting (follow-up sequence)
    SCHEDULED_MESSAGES_LIST_ERROR: 'SCHEDULED_MESSAGES_LIST_ERROR',
    SCHEDULED_MESSAGE_NOT_FOUND: 'SCHEDULED_MESSAGE_NOT_FOUND',
    SCHEDULED_MESSAGE_NOT_PENDING: 'SCHEDULED_MESSAGE_NOT_PENDING',
    SCHEDULED_MESSAGE_UPDATE_ERROR: 'SCHEDULED_MESSAGE_UPDATE_ERROR',
    SCHEDULED_MESSAGE_SEND_NOW_ERROR: 'SCHEDULED_MESSAGE_SEND_NOW_ERROR',
    SCHEDULED_MESSAGE_DELETE_ERROR: 'SCHEDULED_MESSAGE_DELETE_ERROR',
    CANDIDATE_OPTED_OUT: 'CANDIDATE_OPTED_OUT',
    // Per-candidate reminders (Phase B)
    CANDIDATE_REMINDERS_LIST_ERROR: 'CANDIDATE_REMINDERS_LIST_ERROR',
    CANDIDATE_REMINDER_ADD_ERROR: 'CANDIDATE_REMINDER_ADD_ERROR',
    CANDIDATE_REMINDER_UPDATE_ERROR: 'CANDIDATE_REMINDER_UPDATE_ERROR',
    CANDIDATE_REMINDER_DELETE_ERROR: 'CANDIDATE_REMINDER_DELETE_ERROR',
    REMINDER_NOT_FOUND: 'REMINDER_NOT_FOUND',
    APPLICATION_NOT_FOUND: 'APPLICATION_NOT_FOUND',
    // Stripe billing
    INVALID_PLAN: 'INVALID_PLAN',
    INVALID_BILLING_PERIOD: 'INVALID_BILLING_PERIOD',
    MISSING_URLS: 'MISSING_URLS',
    MISSING_URL: 'MISSING_URL',
    NO_STRIPE_CUSTOMER: 'NO_STRIPE_CUSTOMER',
    // Outreach (sales console)
    OUTREACH_NO_EMAIL: 'OUTREACH_NO_EMAIL',
    OUTREACH_NO_ORG: 'OUTREACH_NO_ORG',
    OUTREACH_NO_SEAT: 'OUTREACH_NO_SEAT',
    OUTREACH_NOT_ADMIN: 'OUTREACH_NOT_ADMIN',
    OUTREACH_BAD_EMAIL: 'OUTREACH_BAD_EMAIL',
    OUTREACH_BAD_BODY: 'OUTREACH_BAD_BODY',
    OUTREACH_SEAT_NOT_FOUND: 'OUTREACH_SEAT_NOT_FOUND',
    OUTREACH_SELF_DELETE: 'OUTREACH_SELF_DELETE',
    OUTREACH_LEAD_NOT_FOUND: 'OUTREACH_LEAD_NOT_FOUND',
    OUTREACH_LEAD_UPDATE_FAILED: 'OUTREACH_LEAD_UPDATE_FAILED',
    OUTREACH_IMPORT_FAILED: 'OUTREACH_IMPORT_FAILED',
    OUTREACH_COMPANY_NOT_FOUND: 'OUTREACH_COMPANY_NOT_FOUND',
    OUTREACH_COMPANY_HAS_LEADS: 'OUTREACH_COMPANY_HAS_LEADS',
    OUTREACH_DUPLICATE_EMAIL: 'OUTREACH_DUPLICATE_EMAIL',
    OUTREACH_DUPLICATE_LEAD: 'OUTREACH_DUPLICATE_LEAD',
    OUTREACH_DUPLICATE_DOMAIN: 'OUTREACH_DUPLICATE_DOMAIN',
    OUTREACH_GMAIL_NOT_CONFIGURED: 'OUTREACH_GMAIL_NOT_CONFIGURED',
    OUTREACH_GMAIL_CONNECT_FAILED: 'OUTREACH_GMAIL_CONNECT_FAILED',
    OUTREACH_GMAIL_INVALID_STATE: 'OUTREACH_GMAIL_INVALID_STATE',
    OUTREACH_GMAIL_NOT_CONNECTED: 'OUTREACH_GMAIL_NOT_CONNECTED',
    OUTREACH_CAMPAIGN_NOT_FOUND: 'OUTREACH_CAMPAIGN_NOT_FOUND',
    OUTREACH_CAMPAIGN_OVERLAP: 'OUTREACH_CAMPAIGN_OVERLAP',
    OUTREACH_CAMPAIGN_INVALID_STATUS: 'OUTREACH_CAMPAIGN_INVALID_STATUS',
    OUTREACH_MESSAGE_NOT_FOUND: 'OUTREACH_MESSAGE_NOT_FOUND',
    OUTREACH_MESSAGE_NOT_PENDING: 'OUTREACH_MESSAGE_NOT_PENDING',
    OUTREACH_GROUP_NOT_FOUND: 'OUTREACH_GROUP_NOT_FOUND',
    OUTREACH_DUPLICATE_GROUP: 'OUTREACH_DUPLICATE_GROUP',
    OUTREACH_APIFY_NOT_CONFIGURED: 'OUTREACH_APIFY_NOT_CONFIGURED',
    OUTREACH_APIFY_ERROR: 'OUTREACH_APIFY_ERROR',
    OUTREACH_VALIDATION: 'OUTREACH_VALIDATION',
    OUTREACH_AUDIO_NOT_FOUND: 'OUTREACH_AUDIO_NOT_FOUND',
    OUTREACH_TWILIO_ERROR: 'OUTREACH_TWILIO_ERROR',
    OUTREACH_VOICE_UNAVAILABLE: 'OUTREACH_VOICE_UNAVAILABLE',
    OUTREACH_CALL_NOT_FOUND: 'OUTREACH_CALL_NOT_FOUND',
    OUTREACH_SEND_FAILED: 'OUTREACH_SEND_FAILED',
    OUTREACH_JOB_NOT_FOUND: 'OUTREACH_JOB_NOT_FOUND',
    OUTREACH_JOB_NOT_PAUSED: 'OUTREACH_JOB_NOT_PAUSED',
    // Meta Lead Ads integration
    META_LEAD_ADS_DISABLED: 'META_LEAD_ADS_DISABLED',
    META_LEAD_ADS_NOT_CONFIGURED: 'META_LEAD_ADS_NOT_CONFIGURED',
    META_LEAD_ADS_NOT_CONNECTED: 'META_LEAD_ADS_NOT_CONNECTED',
    META_LEAD_ADS_RECONNECT_REQUIRED: 'META_LEAD_ADS_RECONNECT_REQUIRED',
    META_LEAD_ADS_PAGE_NOT_FOUND: 'META_LEAD_ADS_PAGE_NOT_FOUND',
    /** Another Stanor workspace already receives this Page's leads. */
    META_LEAD_ADS_PAGE_TAKEN: 'META_LEAD_ADS_PAGE_TAKEN',
    META_LEAD_ADS_FORM_NOT_FOUND: 'META_LEAD_ADS_FORM_NOT_FOUND',
    META_LEAD_ADS_INVALID_MAPPING: 'META_LEAD_ADS_INVALID_MAPPING',
    META_LEAD_ADS_LEAD_NOT_FOUND: 'META_LEAD_ADS_LEAD_NOT_FOUND',
    /** Meta refused or failed a request (permissions, rate limits, outages). */
    META_LEAD_ADS_META_ERROR: 'META_LEAD_ADS_META_ERROR',
    META_LEAD_ADS_ERROR: 'META_LEAD_ADS_ERROR',
    // Recruiter calling
    VOICE_VALIDATION: 'VOICE_VALIDATION',
    VOICE_FORBIDDEN: 'VOICE_FORBIDDEN',
    VOICE_NOT_FOUND: 'VOICE_NOT_FOUND',
    VOICE_CONFLICT: 'VOICE_CONFLICT',
    /** Calling, or the capture asked for, is turned off or not set up for the workspace. */
    VOICE_UNAVAILABLE: 'VOICE_UNAVAILABLE',
    /** Buying numbers isn't turned on for this deployment. */
    VOICE_PURCHASE_DISABLED: 'VOICE_PURCHASE_DISABLED',
    /** Too many verification calls lately; try again later. */
    VOICE_RATE_LIMIT: 'VOICE_RATE_LIMIT',
    /** Twilio refused or failed a request. */
    VOICE_PROVIDER_ERROR: 'VOICE_PROVIDER_ERROR',
};
/**
 * Custom API Error class with code and message
 * Use this for typed error handling in client code
 */
export class ApiError extends Error {
    constructor(code, message, details) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.details = details;
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }
    }
    static fromResponse(response) {
        return new ApiError(response.code, response.error, response.details);
    }
    static isApiError(error) {
        return error instanceof ApiError;
    }
    /**
     * Get a user-friendly error message for UI display
     */
    toUserFriendlyError() {
        // Check if we have a specific message for this error code
        const friendlyError = USER_FRIENDLY_MESSAGES[this.code];
        if (friendlyError) {
            return friendlyError;
        }
        // Default fallback using the server's error message
        return {
            title: 'Request failed',
            message: this.message || 'An error occurred. Please try again.',
        };
    }
    /**
     * Convert any error to a user-friendly message
     * Handles both ApiError and generic errors (network issues, etc.)
     */
    static toUserFriendlyError(error) {
        // If it's an ApiError, use its method
        if (ApiError.isApiError(error)) {
            return error.toUserFriendlyError();
        }
        // Handle non-API errors (network issues, etc.)
        const errorMessage = error instanceof Error ? error.message : String(error);
        const lowerMessage = errorMessage.toLowerCase();
        if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('failed to fetch') || lowerMessage.includes('timeout')) {
            return {
                title: 'Connection issue',
                message: 'Unable to reach the server. Please check your internet connection and try again.',
            };
        }
        if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many requests') || lowerMessage.includes('429')) {
            return {
                title: 'Too many requests',
                message: 'You\'ve made too many requests. Please wait a moment and try again.',
            };
        }
        // Generic fallback
        return {
            title: 'Something went wrong',
            message: 'We couldn\'t process your request. Please try again.',
        };
    }
}
// Map of error codes to user-friendly messages
const USER_FRIENDLY_MESSAGES = {
    // Authentication & Authorization
    [ErrorCodes.ADMIN_REQUIRED]: {
        title: 'Admin access required',
        message: 'This action requires administrator privileges. Please contact your admin if you need access.',
    },
    [ErrorCodes.USER_NOT_AUTHORIZED]: {
        title: 'Session expired',
        message: 'Your session has expired. Please refresh the page and sign in again.',
    },
    [ErrorCodes.EMAIL_REQUIRED]: {
        title: 'Email required',
        message: 'Please provide a valid email address to continue.',
    },
    [ErrorCodes.USER_CREATION_ERROR]: {
        title: 'Could not create account',
        message: 'We couldn\'t create your account. Please try again or contact support if the issue persists.',
    },
    [ErrorCodes.USER_LOGIN_ERROR]: {
        title: 'Login failed',
        message: 'We couldn\'t sign you in. Please check your credentials and try again.',
    },
    [ErrorCodes.USER_NOT_FOUND]: {
        title: 'Account not found',
        message: 'We couldn\'t find an account with those details. Please check and try again.',
    },
    [ErrorCodes.USER_UPDATE_ERROR]: {
        title: 'Could not update profile',
        message: 'We couldn\'t save your changes. Please try again.',
    },
    [ErrorCodes.USER_DELETE_ERROR]: {
        title: 'Could not delete account',
        message: 'We couldn\'t delete the account. Please try again or contact support.',
    },
    [ErrorCodes.USER_ALREADY_EXISTS]: {
        title: 'Account already exists',
        message: 'An account with this email already exists. Please sign in instead.',
    },
    [ErrorCodes.USER_ALREADY_REGISTERED]: {
        title: 'Already registered',
        message: 'You\'re already registered. Please sign in to continue.',
    },
    [ErrorCodes.PERMISSION_CHECK_ERROR]: {
        title: 'Permission error',
        message: 'We couldn\'t verify your permissions. Please try signing in again.',
    },
    // Job-related errors
    [ErrorCodes.INVALID_JOB_DESCRIPTION]: {
        title: 'Job description too short',
        message: 'Please provide a more detailed job description. Include the job title, responsibilities, requirements, and qualifications for best results.',
    },
    [ErrorCodes.JOB_DESCRIPTION_PARSING_ERROR]: {
        title: 'Could not extract details',
        message: 'We couldn\'t extract the job details automatically. Please try with a clearer job description, or fill in the fields manually below.',
    },
    [ErrorCodes.JOB_CREATION_ERROR]: {
        title: 'Could not create job',
        message: 'We couldn\'t create the job posting. Please check your input and try again.',
    },
    [ErrorCodes.JOB_PARSING_ERROR]: {
        title: 'Could not parse job',
        message: 'We couldn\'t understand the job details. Please try a different format or enter details manually.',
    },
    [ErrorCodes.JOB_FETCH_ERROR]: {
        title: 'Could not load jobs',
        message: 'We couldn\'t load the jobs. Please try refreshing the page.',
    },
    [ErrorCodes.ORGS_FETCH_ERROR]: {
        title: 'Could not load organizations',
        message: 'We couldn\'t load the organizations. Please try refreshing the page.',
    },
    [ErrorCodes.JOB_UPDATE_ERROR]: {
        title: 'Could not update job',
        message: 'We couldn\'t save your changes to the job. Please try again.',
    },
    [ErrorCodes.JOB_UPDATE_FORBIDDEN]: {
        title: 'Cannot edit this job',
        message: 'You don\'t have permission to edit this job posting.',
    },
    [ErrorCodes.JOB_DELETION_ERROR]: {
        title: 'Could not delete job',
        message: 'We couldn\'t delete the job. Please try again.',
    },
    [ErrorCodes.JOB_DELETE_FORBIDDEN]: {
        title: 'Cannot delete this job',
        message: 'You don\'t have permission to delete this job posting.',
    },
    [ErrorCodes.JOB_OPTIMIZATION_ERROR]: {
        title: 'Optimization failed',
        message: 'We couldn\'t optimize the job posting. Please try again.',
    },
    [ErrorCodes.OPTIMIZATION_QUESTIONS_ERROR]: {
        title: 'Could not generate questions',
        message: 'We couldn\'t generate optimization questions. Please try again.',
    },
    [ErrorCodes.AI_PARSING_FAILED]: {
        title: 'AI processing failed',
        message: 'Our AI couldn\'t process this content. Please try with clearer text or enter details manually.',
    },
    // Candidate-related errors
    [ErrorCodes.CANDIDATE_NOT_FOUND]: {
        title: 'Candidate not found',
        message: 'The candidate you\'re looking for doesn\'t exist or has been removed.',
    },
    [ErrorCodes.CANDIDATE_JOB_NOT_FOUND]: {
        title: 'Application not found',
        message: 'This candidate doesn\'t have an application for the specified job.',
    },
    [ErrorCodes.CANDIDATE_FETCH_ERROR]: {
        title: 'Could not load candidate',
        message: 'We couldn\'t load the candidate details. Please try refreshing the page.',
    },
    [ErrorCodes.CANDIDATE_CREATION_ERROR]: {
        title: 'Could not add candidate',
        message: 'We couldn\'t add this candidate. Please check the information and try again.',
    },
    [ErrorCodes.CANDIDATES_FETCH_ERROR]: {
        title: 'Could not load candidates',
        message: 'We couldn\'t load the candidates. Please try refreshing the page.',
    },
    // Resume parsing errors
    [ErrorCodes.NO_PDF_DATA]: {
        title: 'No PDF found',
        message: 'Please upload a valid PDF file.',
    },
    [ErrorCodes.PDF_PARSE_ERROR]: {
        title: 'Could not read PDF',
        message: 'We couldn\'t read this PDF file. Please make sure it\'s not corrupted or password-protected.',
    },
    [ErrorCodes.PDF_PARSING_FAILED]: {
        title: 'PDF processing failed',
        message: 'We couldn\'t extract information from this PDF. Please try a different file or enter details manually.',
    },
    [ErrorCodes.NO_DOC_DATA]: {
        title: 'No document found',
        message: 'Please upload a valid Word document (.doc or .docx).',
    },
    [ErrorCodes.DOC_PARSE_ERROR]: {
        title: 'Could not read document',
        message: 'We couldn\'t read this document. Please make sure it\'s a valid Word file.',
    },
    [ErrorCodes.DOC_PARSING_FAILED]: {
        title: 'Document processing failed',
        message: 'We couldn\'t extract information from this document. Please try a different file or enter details manually.',
    },
    [ErrorCodes.NO_FILES]: {
        title: 'No files uploaded',
        message: 'Please select at least one file to upload.',
    },
    [ErrorCodes.BATCH_PARSE_ERROR]: {
        title: 'Batch processing failed',
        message: 'We couldn\'t process some of the uploaded files. Please check the files and try again.',
    },
    [ErrorCodes.PARSE_ERROR]: {
        title: 'Processing failed',
        message: 'We couldn\'t process this content. Please try again or use a different format.',
    },
    [ErrorCodes.PARSING_ERROR]: {
        title: 'Could not parse content',
        message: 'We couldn\'t understand this content. Please check the format and try again.',
    },
    [ErrorCodes.TEXT_PARSING_FAILED]: {
        title: 'Could not parse text',
        message: 'We couldn\'t extract information from the provided text. Please check the format.',
    },
    [ErrorCodes.URL_PARSING_FAILED]: {
        title: 'Could not parse URL',
        message: 'We couldn\'t extract information from this URL. Please check that it\'s a valid job posting page.',
    },
    [ErrorCodes.URL_FETCH_FAILED]: {
        title: 'Could not access URL',
        message: 'We couldn\'t access this URL. Please check that it\'s correct and publicly accessible.',
    },
    // Validation errors
    [ErrorCodes.INVALID_MANUAL_PAYLOAD]: {
        title: 'Invalid input',
        message: 'Some required fields are missing or invalid. Please check your input.',
    },
    [ErrorCodes.INVALID_PAYLOAD_TYPE]: {
        title: 'Invalid data format',
        message: 'The data format is not supported. Please check your input.',
    },
    [ErrorCodes.INVALID_SOURCE_TYPE]: {
        title: 'Invalid source',
        message: 'This source type is not supported. Please use a different method.',
    },
    [ErrorCodes.INVALID_PARSE_PAYLOAD]: {
        title: 'Invalid content',
        message: 'The content couldn\'t be processed. Please check the format.',
    },
    [ErrorCodes.INVALID_MULTIPART]: {
        title: 'Upload error',
        message: 'There was a problem with the file upload. Please try again.',
    },
    [ErrorCodes.INVALID_REQUEST]: {
        title: 'Invalid request',
        message: 'The request couldn\'t be processed. Please check your input and try again.',
    },
    [ErrorCodes.INVALID_TEXT_PAYLOAD]: {
        title: 'Invalid text',
        message: 'The text content is invalid or empty. Please provide valid content.',
    },
    [ErrorCodes.INVALID_PDF_PAYLOAD]: {
        title: 'Invalid PDF',
        message: 'The PDF file is invalid. Please upload a valid PDF document.',
    },
    [ErrorCodes.INVALID_DOC_PAYLOAD]: {
        title: 'Invalid document',
        message: 'The document is invalid. Please upload a valid Word file.',
    },
    [ErrorCodes.INVALID_URL_PAYLOAD]: {
        title: 'Invalid URL',
        message: 'Please provide a valid URL starting with http:// or https://.',
    },
    [ErrorCodes.INVALID_STATE]: {
        title: 'Invalid state',
        message: 'This action can\'t be performed in the current state.',
    },
    [ErrorCodes.INVALID_UPDATES]: {
        title: 'Invalid changes',
        message: 'Some of the changes couldn\'t be applied. Please check your input.',
    },
    [ErrorCodes.INVALID_CONFIRMATION]: {
        title: 'Confirmation required',
        message: 'Please confirm this action by entering the required phrase.',
    },
    [ErrorCodes.UNSUPPORTED_CONTENT_TYPE]: {
        title: 'Unsupported file type',
        message: 'This file type is not supported. Please use PDF, DOC, or DOCX files.',
    },
    [ErrorCodes.UNSUPPORTED_SOURCE_TYPE]: {
        title: 'Unsupported source',
        message: 'This source type is not supported.',
    },
    [ErrorCodes.STREAM_ERROR]: {
        title: 'Connection error',
        message: 'The connection was interrupted. Please try again.',
    },
    // Interview & Questions
    [ErrorCodes.MISSING_JOB_ID]: {
        title: 'Job not selected',
        message: 'Please select a job to continue.',
    },
    [ErrorCodes.AI_QUESTIONS_DISABLED]: {
        title: 'AI questions disabled',
        message: 'AI-generated questions are currently disabled. Please contact your admin.',
    },
    [ErrorCodes.QUALIFICATION_QUESTIONS_ERROR]: {
        title: 'Could not generate questions',
        message: 'We couldn\'t generate qualification questions. Please try again.',
    },
    [ErrorCodes.QUESTIONS_STATUS_ERROR]: {
        title: 'Could not get status',
        message: 'We couldn\'t retrieve the questions status. Please refresh the page.',
    },
    [ErrorCodes.INTERVIEW_INVITATION_ERROR]: {
        title: 'Could not send invitation',
        message: 'We couldn\'t send the interview invitation. Please try again.',
    },
    [ErrorCodes.INTERVIEW_CANCELLATION_ERROR]: {
        title: 'Could not cancel interview',
        message: 'We couldn\'t cancel the interview. Please try again.',
    },
    // Messaging
    [ErrorCodes.MESSAGE_SENDING_ERROR]: {
        title: 'Message not sent',
        message: 'We couldn\'t send your message. Please try again.',
    },
    [ErrorCodes.MESSAGE_FETCH_ERROR]: {
        title: 'Could not load messages',
        message: 'We couldn\'t load the messages. Please try refreshing.',
    },
    [ErrorCodes.CHAT_ID_REQUIRED]: {
        title: 'Chat not found',
        message: 'Please select a conversation to continue.',
    },
    [ErrorCodes.CHAT_NOT_FOUND]: {
        title: 'Conversation not found',
        message: 'This conversation doesn\'t exist or has been deleted.',
    },
    [ErrorCodes.CHAT_ACCESS_FORBIDDEN]: {
        title: 'Conversation unavailable',
        message: 'This conversation belongs to another workspace.',
    },
    // State & Data
    [ErrorCodes.MISSING_STATE]: {
        title: 'Session error',
        message: 'Your session state is missing. Please refresh the page.',
    },
    [ErrorCodes.STATE_UPDATE_ERROR]: {
        title: 'Update failed',
        message: 'We couldn\'t update the state. Please try again.',
    },
    [ErrorCodes.DATA_SUMMARY_ERROR]: {
        title: 'Could not load summary',
        message: 'We couldn\'t load your data summary. Please try again.',
    },
    [ErrorCodes.DATA_DELETION_ERROR]: {
        title: 'Deletion failed',
        message: 'We couldn\'t delete your data. Please try again or contact support.',
    },
    // Allowed Users
    [ErrorCodes.ALLOWED_USER_ADD_ERROR]: {
        title: 'Could not add user',
        message: 'We couldn\'t add this user to the allowed list. Please try again.',
    },
    [ErrorCodes.ALLOWED_USERS_FETCH_ERROR]: {
        title: 'Could not load users',
        message: 'We couldn\'t load the allowed users list. Please try refreshing.',
    },
    [ErrorCodes.ALLOWED_USER_REMOVE_ERROR]: {
        title: 'Could not remove user',
        message: 'We couldn\'t remove this user from the allowed list. Please try again.',
    },
    // New Authentication errors
    [ErrorCodes.NO_TOKEN]: {
        title: 'Not signed in',
        message: 'Please sign in to continue.',
    },
    [ErrorCodes.TOKEN_EXPIRED]: {
        title: 'Session expired',
        message: 'Your session has expired. Please sign in again.',
    },
    [ErrorCodes.TOKEN_REVOKED]: {
        title: 'Session revoked',
        message: 'Your session has been revoked. Please sign in again.',
    },
    [ErrorCodes.INVALID_TOKEN]: {
        title: 'Invalid session',
        message: 'Your session is invalid. Please sign in again.',
    },
    [ErrorCodes.AUTH_SERVICE_ERROR]: {
        title: 'Authentication error',
        message: 'We couldn\'t verify your identity. Please try signing in again.',
    },
    [ErrorCodes.AUTHENTICATION_REQUIRED]: {
        title: 'Sign in required',
        message: 'Please sign in to access this feature.',
    },
    // Job errors
    [ErrorCodes.JOB_NOT_FOUND]: {
        title: 'Job not found',
        message: 'The job you\'re looking for doesn\'t exist or has been removed.',
    },
    // Additional Candidate errors
    [ErrorCodes.CANDIDATE_DELETE_FORBIDDEN]: {
        title: 'Cannot delete candidate',
        message: 'You don\'t have permission to delete this candidate.',
    },
    [ErrorCodes.CANDIDATE_DELETE_ERROR]: {
        title: 'Could not delete candidate',
        message: 'We couldn\'t delete this candidate. Please try again.',
    },
    [ErrorCodes.CANDIDATE_SCORE_ERROR]: {
        title: 'Scoring failed',
        message: 'We couldn\'t score this candidate. Please try again.',
    },
    [ErrorCodes.CANDIDATE_UPDATE_FORBIDDEN]: {
        title: 'Cannot update candidate',
        message: 'You don\'t have permission to update this candidate.',
    },
    [ErrorCodes.CANDIDATE_NOT_ASSOCIATED_WITH_JOB]: {
        title: 'Candidate not in job',
        message: 'This candidate is not associated with this job.',
    },
    [ErrorCodes.CANDIDATE_ALREADY_ASSOCIATED]: {
        title: 'Already added',
        message: 'This candidate is already associated with this job.',
    },
    [ErrorCodes.CANDIDATE_JOB_ASSOCIATION_ERROR]: {
        title: 'Could not add to job',
        message: 'We couldn\'t add this candidate to the job. Please try again.',
    },
    [ErrorCodes.SCORE_UPDATE_ERROR]: {
        title: 'Could not update score',
        message: 'We couldn\'t update the candidate\'s score. Please try again.',
    },
    [ErrorCodes.HIRING_STATUS_UPDATE_ERROR]: {
        title: 'Could not update status',
        message: 'We couldn\'t update the hiring status. Please try again.',
    },
    // Additional Validation errors
    [ErrorCodes.INVALID_SCORE]: {
        title: 'Invalid score',
        message: 'Please enter a valid score between 0 and 10.',
    },
    [ErrorCodes.INVALID_STATUS]: {
        title: 'Invalid status',
        message: 'Please select a valid status.',
    },
    [ErrorCodes.MISSING_IDS]: {
        title: 'Missing information',
        message: 'Required identifiers are missing. Please try again.',
    },
    // Admin & Users errors
    [ErrorCodes.USERS_FETCH_ERROR]: {
        title: 'Could not load users',
        message: 'We couldn\'t load the users list. Please try refreshing.',
    },
    [ErrorCodes.UPDATE_USER_ERROR]: {
        title: 'Could not update user',
        message: 'We couldn\'t update this user. Please try again.',
    },
    [ErrorCodes.GET_USER_DETAILS_ERROR]: {
        title: 'Could not load user details',
        message: 'We couldn\'t load the user details. Please try refreshing.',
    },
    [ErrorCodes.USER_SNAPSHOT_ERROR]: {
        title: 'Could not load user snapshot',
        message: 'We couldn\'t build the admin user snapshot. Please try again.',
    },
    // Settings errors
    [ErrorCodes.SETTINGS_FETCH_ERROR]: {
        title: 'Could not load settings',
        message: 'We couldn\'t load your settings. Please try refreshing.',
    },
    [ErrorCodes.SETTINGS_UPDATE_REQUIRED]: {
        title: 'Settings required',
        message: 'Please provide settings to update.',
    },
    [ErrorCodes.SETTINGS_UPDATE_ERROR]: {
        title: 'Could not save settings',
        message: 'We couldn\'t save your settings. Please try again.',
    },
    // Feedback errors
    [ErrorCodes.FEEDBACK_SUBMISSION_ERROR]: {
        title: 'Could not submit feedback',
        message: 'We couldn\'t submit your feedback. Please try again.',
    },
    // System errors
    [ErrorCodes.INTERNAL_ERROR]: {
        title: 'Something went wrong',
        message: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
    },
};
/** "an automatic retry" / "2 automatic retries". */
export function describeAutomaticRetries(attempts) {
    return attempts === 1 ? 'an automatic retry' : `${attempts} automatic retries`;
}
/**
 * What an outbound candidate message is for. Decides which channels may carry it: a message
 * the candidate is expected to answer needs a channel Stanor reads replies on.
 */
export var CandidateMessageIntent;
(function (CandidateMessageIntent) {
    CandidateMessageIntent["ApplicationAcknowledgement"] = "application_acknowledgement";
    CandidateMessageIntent["ScreeningQuestion"] = "screening_question";
    /** Replies the conversation engine sends to the candidate's messages. */
    CandidateMessageIntent["ConversationReply"] = "conversation_reply";
    CandidateMessageIntent["ResponseFollowUp"] = "response_follow_up";
    /** Interview invitations and rescheduling offers that ask the candidate to pick a time. */
    CandidateMessageIntent["InterviewScheduling"] = "interview_scheduling";
    /** Confirmations of a booked or changed interview time. */
    CandidateMessageIntent["InterviewUpdate"] = "interview_update";
    CandidateMessageIntent["InterviewReminder"] = "interview_reminder";
    CandidateMessageIntent["Offer"] = "offer";
    /** Rejections, "we've received your answers" and other updates that don't need an answer. */
    CandidateMessageIntent["StatusUpdate"] = "status_update";
    CandidateMessageIntent["RecruiterMessage"] = "recruiter_message";
})(CandidateMessageIntent || (CandidateMessageIntent = {}));
/** A tab's id: letters, digits, `-` and `_`, up to 64 (a UUID is one). */
export function isNotificationTabId(value) {
    return typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value);
}
/** Messages a page of a conversation holds: the newest page when it opens, then each "Show earlier messages". */
export const CHAT_MESSAGES_PAGE_SIZE = 50;
export var MessageTransport;
(function (MessageTransport) {
    MessageTransport["Sms"] = "sms";
    MessageTransport["Email"] = "email";
    MessageTransport["WhatsApp"] = "whatsapp";
})(MessageTransport || (MessageTransport = {}));
export var PendingSendIntent;
(function (PendingSendIntent) {
    PendingSendIntent["QualificationQuestions"] = "qualification_questions";
    PendingSendIntent["InterviewInvitation"] = "interview_invitation";
    PendingSendIntent["CustomMessage"] = "custom_message";
})(PendingSendIntent || (PendingSendIntent = {}));
export var PendingSendBlocker;
(function (PendingSendBlocker) {
    PendingSendBlocker["PhoneUnverified"] = "phone_unverified";
    /**
     * Paid-tier recruiter has not yet affirmed they have permission to text
     * THIS candidate. Resolved by the recruiter accepting the SMS consent
     * dialog, which calls `POST /candidates/consent` and persists
     * `consentApproved = { approved: true, at, by }` on each candidate doc.
     */
    PendingSendBlocker["ConsentRequired"] = "consent_required";
    PendingSendBlocker["CreditsExhausted"] = "credits_exhausted";
})(PendingSendBlocker || (PendingSendBlocker = {}));
// Enhanced CreateJobData interface
// ───────────────────────────────────────────────────────────────
// Branded public job postings — presentation layer (all optional).
// Absent branding/config ⇒ current default look & behavior.
// ───────────────────────────────────────────────────────────────
/** Visual presets for the public job/apply page. `Default` = current look. */
export var PublicPageTheme;
(function (PublicPageTheme) {
    PublicPageTheme["Default"] = "default";
    PublicPageTheme["ElegantSalon"] = "elegant-salon";
    PublicPageTheme["ModernMinimal"] = "modern-minimal";
    PublicPageTheme["BoldHourly"] = "bold-hourly";
})(PublicPageTheme || (PublicPageTheme = {}));
// Job types enum
export var JobType;
(function (JobType) {
    JobType["FullTime"] = "FullTime";
    JobType["PartTime"] = "PartTime";
    JobType["Contract"] = "Contract";
    JobType["Temporary"] = "Temporary";
    JobType["Seasonal"] = "Seasonal";
    JobType["Internship"] = "Internship";
})(JobType || (JobType = {}));
// Employment type enum (more specific than JobType)
export var EmploymentType;
(function (EmploymentType) {
    EmploymentType["FullTime"] = "FullTime";
    EmploymentType["PartTime"] = "PartTime";
    EmploymentType["Contract"] = "Contract";
    EmploymentType["Temporary"] = "Temporary";
    EmploymentType["Freelance"] = "Freelance";
    EmploymentType["Internship"] = "Internship";
})(EmploymentType || (EmploymentType = {}));
// Shift type enum
export var ShiftType;
(function (ShiftType) {
    ShiftType["Day"] = "Day";
    ShiftType["Night"] = "Night";
    ShiftType["Variable"] = "Variable";
    ShiftType["StandardBusinessHours"] = "StandardBusinessHours";
})(ShiftType || (ShiftType = {}));
// Job location type enum
export var JobLocationType;
(function (JobLocationType) {
    JobLocationType["InPerson"] = "InPerson";
    JobLocationType["OnRoad"] = "OnRoad";
    JobLocationType["Hybrid"] = "Hybrid";
    JobLocationType["FullyRemote"] = "FullyRemote";
})(JobLocationType || (JobLocationType = {}));
// ───────────────────────────────────────────────────────────────
// Display labels — shared across all apps so UIs stay consistent.
// ───────────────────────────────────────────────────────────────
export const JOB_TYPE_LABELS = {
    [JobType.FullTime]: 'Full Time',
    [JobType.PartTime]: 'Part Time',
    [JobType.Contract]: 'Contract',
    [JobType.Temporary]: 'Temporary',
    [JobType.Seasonal]: 'Seasonal',
    [JobType.Internship]: 'Internship',
};
export const SHIFT_TYPE_LABELS = {
    [ShiftType.StandardBusinessHours]: 'Standard Business Hours',
    [ShiftType.Day]: 'Day Shift',
    [ShiftType.Night]: 'Night Shift',
    [ShiftType.Variable]: 'Variable Shift',
};
export const JOB_LOCATION_TYPE_LABELS = {
    [JobLocationType.InPerson]: 'In Person',
    [JobLocationType.Hybrid]: 'Hybrid',
    [JobLocationType.FullyRemote]: 'Fully Remote',
    [JobLocationType.OnRoad]: 'On Road',
};
// Experience level enum
export var ExperienceLevel;
(function (ExperienceLevel) {
    ExperienceLevel["EntryLevel"] = "EntryLevel";
    ExperienceLevel["MidLevel"] = "MidLevel";
    ExperienceLevel["SeniorLevel"] = "SeniorLevel";
    ExperienceLevel["ExecutiveLevel"] = "ExecutiveLevel";
})(ExperienceLevel || (ExperienceLevel = {}));
// ─────────────────────────────────────────────────────────────────────────────
// Recruiting pipeline stages (see docs/dynamic-pipeline-stages-design.md)
//
// The recruiter-facing pipeline is a first-class, configurable entity: an ordered
// set of stages stored per-workspace (default) and optionally per-job (override),
// resolved via the same cascade as `interviewSlotTemplate` / `qualificationWorkflow`.
// A stage is either a plain `manual` bucket the recruiter fully controls, or an
// `automated` stage bound to exactly one built-in `StageCapability` — the recruiter
// PICKS from this catalog and cannot author new automations.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Built-in automations a stage can bind to. Each maps to a server entry-action +
 * a frontend column renderer (the capability registry). Recruiters select from
 * this catalog; they cannot author new capabilities.
 */
export var StageCapability;
(function (StageCapability) {
    StageCapability["SmsScreening"] = "SmsScreening";
    /** Single interview stage — scheduling, negotiation, confirmation, and completion live inside it. */
    StageCapability["Interview"] = "Interview";
    StageCapability["Offer"] = "Offer";
})(StageCapability || (StageCapability = {}));
/** True for the capability that represents the interview stage. */
export function isInterviewCapability(capability) {
    return capability === StageCapability.Interview;
}
/** Whether landing in a stage ends the candidate's journey for the job. */
export var StageTerminality;
(function (StageTerminality) {
    StageTerminality["Active"] = "Active";
    StageTerminality["Won"] = "Won";
    StageTerminality["Lost"] = "Lost";
})(StageTerminality || (StageTerminality = {}));
/**
 * The platform default pipeline — Applied → SMS Screening → Interview → Offer. Seeds
 * `Settings.defaultWorkspaceSettings.pipelineTemplate` (functions/src/defaults.ts)
 * and is the final fallback in `jobService.create` and the backfill migration
 * for legacy or unstamped jobs. Stage ids are stable string literals so the
 * migration backfill lines up.
 */
export const DEFAULT_PIPELINE_TEMPLATE = {
    updatedAtUtc: toUtcMs(0),
    stages: [
        { id: 'applied', kind: 'manual', name: 'Applied', order: 0, color: '#5D87FF', terminality: StageTerminality.Active },
        { id: 'sms_screening', kind: 'automated', capability: StageCapability.SmsScreening, name: 'SMS Screening', order: 1, color: '#49BEFF', terminality: StageTerminality.Active },
        { id: 'interview_booking', kind: 'automated', capability: StageCapability.Interview, name: 'Interview', order: 2, color: '#13DEB9', terminality: StageTerminality.Active },
        { id: 'offer', kind: 'automated', capability: StageCapability.Offer, name: 'Offer', order: 3, color: '#FFAE1F', terminality: StageTerminality.Active },
    ],
};
/** `Job.status` of a job that no longer takes candidates. */
export const JOB_CLOSED_STATUS = 'closed';
/**
 * A commute is meaningful only when the AI resolved a location; unknown is
 * modeled as an ABSENT `distance` (the scorer never emits a placeholder). This
 * is the single "is this a real commute" check, also guarding a blank label.
 */
export function hasKnownDistance(distance) {
    return !!distance?.label?.trim();
}
/** Returns a scoreview with any unknown/sentinel commute stripped (unknown ⇒ no distance). */
export function normalizeScoreViewDistance(scoreview) {
    if (hasKnownDistance(scoreview.distance))
        return scoreview;
    const normalized = { ...scoreview };
    delete normalized.distance;
    return normalized;
}
// Helper to convert ExperienceDate or number to display string
export function formatExperienceDate(date) {
    if (!date)
        return 'Present';
    if (typeof date === 'number') {
        // Check if it's a year (1900-2100) or a timestamp
        if (date >= 1900 && date <= 2100) {
            return String(date);
        }
        // Unix timestamp
        return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    // Structured date object
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (date.month) {
        return `${months[date.month - 1]} ${date.year}`;
    }
    return String(date.year);
}
// Helper to convert ExperienceDate or number to timestamp for calculations
export function experienceDateToTimestamp(date, isEndDate = false) {
    if (!date)
        return Date.now(); // Current for missing end dates
    if (typeof date === 'number') {
        // Check if it's a year (1900-2100) or a timestamp
        if (date >= 1900 && date <= 2100) {
            // Year only - use Jan 1 for start, Dec 31 for end
            return isEndDate
                ? new Date(date, 11, 31).getTime()
                : new Date(date, 0, 1).getTime();
        }
        return date;
    }
    // Structured date object
    const month = (date.month ?? (isEndDate ? 12 : 1)) - 1; // JS months are 0-indexed
    const day = date.day ?? (isEndDate ? 28 : 1); // Safe end-of-month default
    return new Date(date.year, month, day).getTime();
}
export var CandidateCreationSourceType;
(function (CandidateCreationSourceType) {
    CandidateCreationSourceType["Manual"] = "manual";
    CandidateCreationSourceType["Url"] = "url";
    CandidateCreationSourceType["Text"] = "text";
    CandidateCreationSourceType["Pdf"] = "pdf";
    CandidateCreationSourceType["Doc"] = "doc";
})(CandidateCreationSourceType || (CandidateCreationSourceType = {}));
export var MessageDeliveryStatus;
(function (MessageDeliveryStatus) {
    MessageDeliveryStatus["Pending"] = "pending";
    MessageDeliveryStatus["SentToChat"] = "sent_to_chat";
    MessageDeliveryStatus["Delivered"] = "delivered";
    MessageDeliveryStatus["Failed"] = "failed";
})(MessageDeliveryStatus || (MessageDeliveryStatus = {}));
/**
 * Answer input type for a screening question rendered on the public apply form.
 * Absent ⇒ legacy text question asked via SMS after scoring (behavior unchanged).
 */
export var QuestionAnswerType;
(function (QuestionAnswerType) {
    QuestionAnswerType["YesNo"] = "yesno";
    QuestionAnswerType["Select"] = "select";
    QuestionAnswerType["MultiSelect"] = "multiselect";
    QuestionAnswerType["Text"] = "text";
    QuestionAnswerType["LongText"] = "longtext";
    QuestionAnswerType["Url"] = "url";
    QuestionAnswerType["Number"] = "number";
})(QuestionAnswerType || (QuestionAnswerType = {}));
export var CandidateState;
(function (CandidateState) {
    CandidateState["Processing"] = "Processing";
    CandidateState["Initialized"] = "Initialized";
    CandidateState["ResumeScored"] = "ResumeScored";
    CandidateState["QualificationQuestionSent"] = "QualificationQuestionSent";
    CandidateState["QualificationAnswerReceived"] = "QualificationAnswerReceived";
    CandidateState["QualificationScored"] = "QualificationScored";
    CandidateState["InterviewSchedulingSent"] = "InterviewSchedulingSent";
    CandidateState["InterviewScheduled"] = "InterviewScheduled";
    CandidateState["InterviewCompleted"] = "InterviewCompleted";
    CandidateState["PostInterviewThanked"] = "PostInterviewThanked";
    CandidateState["DecisionSent"] = "DecisionSent";
    CandidateState["DecisionResolved"] = "DecisionResolved";
    CandidateState["OfferReady"] = "OfferReady";
    CandidateState["Rejected"] = "Rejected";
})(CandidateState || (CandidateState = {}));
/**
 * Project a `CandidateState` onto its default-pipeline stage id. The single
 * source of the state→stage mapping — used to keep `JobApplication.stageId` in
 * sync at transition sites and as the read-time shim for un-migrated docs.
 * A total map, so adding a `CandidateState` is a compile error until mapped.
 */
const STAGE_ID_BY_STATE = {
    [CandidateState.Processing]: 'applied',
    [CandidateState.Initialized]: 'applied',
    [CandidateState.ResumeScored]: 'applied',
    [CandidateState.QualificationQuestionSent]: 'sms_screening',
    [CandidateState.QualificationAnswerReceived]: 'sms_screening',
    [CandidateState.QualificationScored]: 'sms_screening',
    [CandidateState.InterviewSchedulingSent]: 'interview_booking',
    [CandidateState.InterviewScheduled]: 'interview_booking',
    [CandidateState.InterviewCompleted]: 'interview_booking',
    [CandidateState.PostInterviewThanked]: 'interview_booking',
    [CandidateState.DecisionSent]: 'offer',
    [CandidateState.DecisionResolved]: 'offer',
    [CandidateState.OfferReady]: 'offer',
    [CandidateState.Rejected]: 'applied',
};
export function stageIdForState(state) {
    return STAGE_ID_BY_STATE[state];
}
const STAGE_ANCHOR_BY_STATE = {
    [CandidateState.Processing]: { kind: 'first' },
    [CandidateState.Initialized]: { kind: 'first' },
    [CandidateState.ResumeScored]: { kind: 'first' },
    [CandidateState.QualificationQuestionSent]: { kind: 'capability', capability: StageCapability.SmsScreening },
    [CandidateState.QualificationAnswerReceived]: { kind: 'capability', capability: StageCapability.SmsScreening },
    [CandidateState.QualificationScored]: { kind: 'capability', capability: StageCapability.SmsScreening },
    [CandidateState.InterviewSchedulingSent]: { kind: 'capability', capability: StageCapability.Interview },
    [CandidateState.InterviewScheduled]: { kind: 'capability', capability: StageCapability.Interview },
    [CandidateState.InterviewCompleted]: { kind: 'capability', capability: StageCapability.Interview },
    [CandidateState.PostInterviewThanked]: { kind: 'capability', capability: StageCapability.Interview },
    [CandidateState.DecisionSent]: { kind: 'capability', capability: StageCapability.Offer },
    [CandidateState.DecisionResolved]: { kind: 'capability', capability: StageCapability.Offer },
    [CandidateState.OfferReady]: { kind: 'capability', capability: StageCapability.Offer },
    [CandidateState.Rejected]: { kind: 'first' },
};
/**
 * Resolve the stage a candidate belongs in after a state transition, against the
 * job's *configured* pipeline. Unlike `stageIdForState` (fixed default ids), this
 * projects the state's capability onto the template, searching **from the
 * candidate's current stage forward** so repeated-capability stages (a 2nd
 * interview round) land on the correct instance. Falls back to the first stage
 * carrying the capability, then to the current stage if the template omits it.
 * For an unchanged (default-order) template this returns the same ids as
 * `stageIdForState`, so existing pipelines are unaffected.
 */
export function projectStageId(currentStageId, state, template) {
    const stages = template.stages;
    if (stages.length === 0)
        return currentStageId;
    const anchor = STAGE_ANCHOR_BY_STATE[state];
    if (anchor.kind === 'first')
        return stages[0].id;
    const { capability } = anchor;
    const curIdx = stages.findIndex((s) => s.id === currentStageId);
    for (let i = curIdx >= 0 ? curIdx : 0; i < stages.length; i++) {
        const s = stages[i];
        if (s.kind === 'automated' && s.capability === capability)
            return s.id;
    }
    const firstMatch = stages.find((s) => s.kind === 'automated' && s.capability === capability);
    return firstMatch ? firstMatch.id : currentStageId;
}
/**
 * Validate a recruiter-editable pipeline template. Returns the first structural
 * problem as a human-readable message, or null when valid. Enforced server-side on
 * every workspace/job pipeline write so a bad template can't strand candidates on a
 * stageId the board can't render. Multiple `Interview` stages are allowed so a pipeline
 * can model several interview rounds (e.g. an online screen then an on-site).
 */
export function validatePipelineTemplate(template) {
    const stages = template.stages;
    if (stages.length === 0)
        return 'A pipeline needs at least one stage.';
    const ids = new Set();
    let activeCount = 0;
    const supported = new Set(Object.values(StageCapability));
    for (const s of stages) {
        if (!s.id)
            return 'Every stage needs an id.';
        if (ids.has(s.id))
            return `Duplicate stage id: ${s.id}.`;
        ids.add(s.id);
        if (!s.name.trim())
            return 'Every stage needs a name.';
        if (s.terminality === StageTerminality.Active)
            activeCount++;
        if (s.kind === 'automated' && !supported.has(s.capability)) {
            return `Unsupported stage capability: ${s.capability}.`;
        }
    }
    if (activeCount === 0)
        return 'A pipeline needs at least one active stage.';
    return null;
}
/**
 * The stage immediately following `currentStageId` in the template, or `null` if
 * it's the last (or missing). Used to advance a candidate one column forward on
 * a milestone that isn't a state change — e.g. offer acceptance moving a hired
 * candidate from `offer` into a trailing `accepted`/won stage when one exists.
 */
export function stageIdAfter(currentStageId, template) {
    const idx = template.stages.findIndex((s) => s.id === currentStageId);
    if (idx < 0 || idx + 1 >= template.stages.length)
        return null;
    return template.stages[idx + 1].id;
}
/**
 * The stage a candidate advances INTO for a recruiter forward action of the given
 * `capability`, resolved from the candidate's current position in the ordered
 * pipeline — never from `CandidateState`. This is the stageId-authoritative
 * successor to `projectStageId`: the *action* names the capability it advances to,
 * so the destination is a function of (currentStageId, capability, template) alone.
 *
 * If the current stage already carries the capability (a repeated round — e.g. a
 * candidate in the first interview being booked for a second), the scan starts
 * strictly AFTER the current stage so they move to the *next* same-capability
 * stage. Otherwise it returns the first stage with the capability at or ahead of
 * the current position. Idempotent: when no further same-capability stage exists
 * it returns the current stage, so a repeated call (e.g. the screening
 * question-send loop firing per question) never over-advances a column.
 */
export function advanceStageForCapability(currentStageId, capability, template) {
    const stages = template.stages;
    if (stages.length === 0)
        return currentStageId;
    const curIdx = stages.findIndex((s) => s.id === currentStageId);
    const cur = curIdx >= 0 ? stages[curIdx] : undefined;
    const alreadyInCapability = !!cur && cur.kind === 'automated' && cur.capability === capability;
    const from = alreadyInCapability ? curIdx + 1 : (curIdx >= 0 ? curIdx : 0);
    for (let i = from; i < stages.length; i++) {
        const s = stages[i];
        if (s.kind === 'automated' && s.capability === capability)
            return s.id;
    }
    return currentStageId;
}
/**
 * The recruiter-authored interview invite intro configured on a stage, trimmed.
 * Returns null when the stage isn't an interview-capability stage or has no
 * message set — callers then fall back to the assistant's default invite copy.
 */
export function interviewInviteIntroForStage(template, stageId) {
    const stage = template.stages.find((s) => s.id === stageId);
    if (!stage || stage.kind !== 'automated' || stage.capability !== StageCapability.Interview) {
        return null;
    }
    const msg = stage.inviteMessage?.trim();
    return msg ? msg : null;
}
// Hiring status categories for grouping in UI
export var HiringStatusCategory;
(function (HiringStatusCategory) {
    HiringStatusCategory["Initial"] = "Initial";
    HiringStatusCategory["Assessment"] = "Assessment";
    HiringStatusCategory["Interview"] = "Interview";
    HiringStatusCategory["OfferFinal"] = "Offer/Final";
    HiringStatusCategory["Rejection"] = "Rejection";
    HiringStatusCategory["Other"] = "Other";
})(HiringStatusCategory || (HiringStatusCategory = {}));
// Comprehensive recruiter-controlled hiring status for candidates
export var HiringStatus;
(function (HiringStatus) {
    // Initial stage
    HiringStatus["New"] = "New";
    HiringStatus["NoResponsePhone"] = "NoResponsePhone";
    HiringStatus["PhoneScreened"] = "PhoneScreened";
    HiringStatus["PhoneScreenReject"] = "PhoneScreenReject";
    HiringStatus["ResumeShortlisted"] = "ResumeShortlisted";
    // Assessment stage
    HiringStatus["AssessmentInvited"] = "AssessmentInvited";
    HiringStatus["AssessmentAccepted"] = "AssessmentAccepted";
    HiringStatus["AssessmentScheduled"] = "AssessmentScheduled";
    HiringStatus["AssessmentPassed"] = "AssessmentPassed";
    HiringStatus["AssessmentFailed"] = "AssessmentFailed";
    // Interview stage
    HiringStatus["InterviewInvited"] = "InterviewInvited";
    HiringStatus["InterviewAccepted"] = "InterviewAccepted";
    HiringStatus["InterviewScheduled"] = "InterviewScheduled";
    HiringStatus["InterviewAttended"] = "InterviewAttended";
    HiringStatus["InterviewPassed"] = "InterviewPassed";
    HiringStatus["InterviewFailed"] = "InterviewFailed";
    // Offer/Final stage
    HiringStatus["JobOffered"] = "JobOffered";
    HiringStatus["OfferAccepted"] = "OfferAccepted";
    HiringStatus["OfferRejected"] = "OfferRejected";
    HiringStatus["BackgroundScreening"] = "BackgroundScreening";
    HiringStatus["Joined"] = "Joined";
    // Rejection stage
    HiringStatus["CandidateNoShow"] = "CandidateNoShow";
    HiringStatus["InterviewDeclined"] = "InterviewDeclined";
    HiringStatus["JobOfferReject"] = "JobOfferReject";
    HiringStatus["InternalScreeningReject"] = "InternalScreeningReject";
    HiringStatus["RejectedByClient"] = "RejectedByClient";
    HiringStatus["NotInterested"] = "NotInterested";
    // Other
    HiringStatus["NotJoined"] = "NotJoined";
    HiringStatus["Hold"] = "Hold";
    HiringStatus["InternalHold"] = "InternalHold";
})(HiringStatus || (HiringStatus = {}));
// Hiring status configuration map
export const HIRING_STATUS_CONFIG = {
    // Initial - Blue tones
    [HiringStatus.New]: { label: 'New', color: '#5D87FF', bgColor: '#ECF2FF', category: HiringStatusCategory.Initial },
    [HiringStatus.NoResponsePhone]: { label: 'No Response (Phone)', color: '#7C8FAC', bgColor: '#F2F6FA', category: HiringStatusCategory.Initial },
    [HiringStatus.PhoneScreened]: { label: 'Phone Screened', color: '#49BEFF', bgColor: '#E8F7FF', category: HiringStatusCategory.Initial },
    [HiringStatus.PhoneScreenReject]: { label: 'Phone Screen Reject', color: '#FA896B', bgColor: '#FDEDE8', category: HiringStatusCategory.Initial },
    [HiringStatus.ResumeShortlisted]: { label: 'Resume Shortlisted', color: '#13DEB9', bgColor: '#E6FFFA', category: HiringStatusCategory.Initial },
    // Assessment - Purple tones
    [HiringStatus.AssessmentInvited]: { label: 'Assessment Invited', color: '#7C4DFF', bgColor: '#EDE7F6', category: HiringStatusCategory.Assessment },
    [HiringStatus.AssessmentAccepted]: { label: 'Assessment Accepted', color: '#9575CD', bgColor: '#EDE7F6', category: HiringStatusCategory.Assessment },
    [HiringStatus.AssessmentScheduled]: { label: 'Assessment Scheduled', color: '#7E57C2', bgColor: '#EDE7F6', category: HiringStatusCategory.Assessment },
    [HiringStatus.AssessmentPassed]: { label: 'Assessment Passed', color: '#13DEB9', bgColor: '#E6FFFA', category: HiringStatusCategory.Assessment },
    [HiringStatus.AssessmentFailed]: { label: 'Assessment Failed', color: '#FA896B', bgColor: '#FDEDE8', category: HiringStatusCategory.Assessment },
    // Interview - Cyan tones
    [HiringStatus.InterviewInvited]: { label: '1st Interview Invited', color: '#00BCD4', bgColor: '#E0F7FA', category: HiringStatusCategory.Interview },
    [HiringStatus.InterviewAccepted]: { label: '1st Interview Accepted', color: '#26C6DA', bgColor: '#E0F7FA', category: HiringStatusCategory.Interview },
    [HiringStatus.InterviewScheduled]: { label: '1st Interview Scheduled', color: '#00ACC1', bgColor: '#E0F7FA', category: HiringStatusCategory.Interview },
    [HiringStatus.InterviewAttended]: { label: '1st Interview Attended', color: '#0097A7', bgColor: '#E0F7FA', category: HiringStatusCategory.Interview },
    [HiringStatus.InterviewPassed]: { label: '1st Interview Passed', color: '#13DEB9', bgColor: '#E6FFFA', category: HiringStatusCategory.Interview },
    [HiringStatus.InterviewFailed]: { label: '1st Interview Failed', color: '#FA896B', bgColor: '#FDEDE8', category: HiringStatusCategory.Interview },
    // Offer/Final - Green/Gold tones
    [HiringStatus.JobOffered]: { label: 'Job Offered', color: '#FFAE1F', bgColor: '#FEF5E5', category: HiringStatusCategory.OfferFinal },
    [HiringStatus.OfferAccepted]: { label: 'Offer Accepted', color: '#13DEB9', bgColor: '#E6FFFA', category: HiringStatusCategory.OfferFinal },
    [HiringStatus.OfferRejected]: { label: 'Offer Rejected', color: '#FA896B', bgColor: '#FDEDE8', category: HiringStatusCategory.OfferFinal },
    [HiringStatus.BackgroundScreening]: { label: 'Background Screening', color: '#539BFF', bgColor: '#EBF3FE', category: HiringStatusCategory.OfferFinal },
    [HiringStatus.Joined]: { label: 'Joined', color: '#02b3a9', bgColor: '#E6FFFA', category: HiringStatusCategory.OfferFinal },
    // Rejection - Red/Orange tones
    [HiringStatus.CandidateNoShow]: { label: 'Candidate No Show', color: '#f3704d', bgColor: '#FDEDE8', category: HiringStatusCategory.Rejection },
    [HiringStatus.InterviewDeclined]: { label: 'Interview Declined', color: '#FA896B', bgColor: '#FDEDE8', category: HiringStatusCategory.Rejection },
    [HiringStatus.JobOfferReject]: { label: 'Job Offer Reject', color: '#FA896B', bgColor: '#FDEDE8', category: HiringStatusCategory.Rejection },
    [HiringStatus.InternalScreeningReject]: { label: 'Internal Screening Reject', color: '#f3704d', bgColor: '#FDEDE8', category: HiringStatusCategory.Rejection },
    [HiringStatus.RejectedByClient]: { label: 'Rejected by Client', color: '#f3704d', bgColor: '#FDEDE8', category: HiringStatusCategory.Rejection },
    [HiringStatus.NotInterested]: { label: 'Not Interested', color: '#7C8FAC', bgColor: '#F2F6FA', category: HiringStatusCategory.Rejection },
    // Other - Grey/Amber tones
    [HiringStatus.NotJoined]: { label: 'Not Joined', color: '#5A6A85', bgColor: '#F2F6FA', category: HiringStatusCategory.Other },
    [HiringStatus.Hold]: { label: 'Hold', color: '#FFAE1F', bgColor: '#FEF5E5', category: HiringStatusCategory.Other },
    [HiringStatus.InternalHold]: { label: 'Internal Hold', color: '#ae8e59', bgColor: '#FEF5E5', category: HiringStatusCategory.Other },
};
/**
 * True once the candidate has reached a terminal outcome for this application
 * (rejected, hired/joined, or won't join). Used to flip the Free-tier SMS
 * trial slot to `completed`.
 */
export function isTerminalHiringStatus(status) {
    const meta = HIRING_STATUS_CONFIG[status];
    if (!meta)
        return false;
    if (meta.category === HiringStatusCategory.Rejection)
        return true;
    return status === HiringStatus.Joined || status === HiringStatus.NotJoined;
}
/** When a hiring status was set, whichever field the entry keeps it in. */
export function hiringStatusUpdateTimeUtc(update) {
    return toUtcMs(update.updatedAtUtc ?? update.updatedAt ?? 0);
}
/**
 * Default rejection reasons offered in the reject dialog. Recruiters can also
 * add custom reasons, which persist on `Organization.rejectionReasons`.
 */
export const DEFAULT_REJECTION_REASONS = [
    'Not qualified / missing skills',
    'Lacks license or certification',
    'Location too far',
    'Availability / shift mismatch',
    'Compensation mismatch',
    'Failed screening',
    'No response',
    'Overqualified',
    'Position filled',
    'Duplicate',
];
// Legacy alias for backward compatibility (deprecated, use HiringStatus)
export const RecruiterStatus = HiringStatus;
/** Whether the candidate made the application themselves (so it gets an automatic first contact). */
export function isCandidateMadeApplication(source) {
    return source === 'public_form' || source === 'meta_lead_ads';
}
export var ApplicationAcknowledgementStatus;
(function (ApplicationAcknowledgementStatus) {
    ApplicationAcknowledgementStatus["Pending"] = "Pending";
    ApplicationAcknowledgementStatus["Sending"] = "Sending";
    ApplicationAcknowledgementStatus["Sent"] = "Sent";
    ApplicationAcknowledgementStatus["Failed"] = "Failed";
    ApplicationAcknowledgementStatus["Skipped"] = "Skipped";
})(ApplicationAcknowledgementStatus || (ApplicationAcknowledgementStatus = {}));
export var ResponseExpectationAction;
(function (ResponseExpectationAction) {
    ResponseExpectationAction["QualificationAnswer"] = "qualification_answer";
    ResponseExpectationAction["InterviewSelection"] = "interview_selection";
    ResponseExpectationAction["OfferResponse"] = "offer_response";
})(ResponseExpectationAction || (ResponseExpectationAction = {}));
export var ResponseExpectationStatus;
(function (ResponseExpectationStatus) {
    ResponseExpectationStatus["Active"] = "Active";
    ResponseExpectationStatus["Resolved"] = "Resolved";
    ResponseExpectationStatus["Exhausted"] = "Exhausted";
    ResponseExpectationStatus["Cancelled"] = "Cancelled";
})(ResponseExpectationStatus || (ResponseExpectationStatus = {}));
export var ResponseFollowUpStatus;
(function (ResponseFollowUpStatus) {
    ResponseFollowUpStatus["Scheduled"] = "Scheduled";
    ResponseFollowUpStatus["Sending"] = "Sending";
    ResponseFollowUpStatus["Sent"] = "Sent";
    ResponseFollowUpStatus["Skipped"] = "Skipped";
    ResponseFollowUpStatus["Cancelled"] = "Cancelled";
    ResponseFollowUpStatus["Failed"] = "Failed";
})(ResponseFollowUpStatus || (ResponseFollowUpStatus = {}));
/**
 * Compute a compact denormalized snapshot of follow-up state for a
 * (candidate, job) pair from a flat list of all messages on that pair.
 *
 * Logic:
 *  - `pendingCount`   = count of `Pending`
 *  - `sentCount`      = count of `Sent` + `Delivered`
 *  - `stoppedCount`   = count of `Cancelled` + `Skipped`
 *  - `next`           = earliest pending by `scheduledFor`, if any
 *  - `lastTerminal`   = most recent non-Pending by `updatedAt`
 *  - `lastFailed`     = `lastTerminal?.status === Failed`
 */
export function buildFollowUpSummary(messages) {
    let pendingCount = 0;
    let sentCount = 0;
    let stoppedCount = 0;
    let next;
    let lastTerminal;
    for (const m of messages) {
        if (m.status === ScheduledMessageStatus.Pending) {
            pendingCount++;
            if (!next || m.scheduledForUtc < next.scheduledForUtc)
                next = m;
        }
        else {
            if (m.status === ScheduledMessageStatus.Sent || m.status === ScheduledMessageStatus.Delivered) {
                sentCount++;
            }
            if (m.status === ScheduledMessageStatus.Cancelled || m.status === ScheduledMessageStatus.Skipped) {
                stoppedCount++;
            }
            if (!lastTerminal || m.updatedAtUtc > lastTerminal.updatedAtUtc)
                lastTerminal = m;
        }
    }
    return {
        pendingCount,
        sentCount,
        stoppedCount,
        lastFailed: lastTerminal?.status === ScheduledMessageStatus.Failed,
        next: next ? { touchpointName: next.touchpointName, scheduledForUtc: next.scheduledForUtc } : undefined,
        lastTerminal: lastTerminal ? {
            touchpointName: lastTerminal.touchpointName,
            status: lastTerminal.status,
            sentTimeUtc: lastTerminal.sentTimeUtc,
            failureReason: lastTerminal.failureReason,
        } : undefined,
        updatedAtUtc: nowUtc(),
    };
}
/** Whether the candidate granted SMS consent themselves (on our application form or a Meta form). */
export function isApplicantGrantedSmsConsent(consent) {
    return consent.approved && 'source' in consent;
}
/**
 * Returns the effective list of qualification questions for a candidate's
 * application. The candidate's `qualificationQuestions` is a SPARSE list
 * of per-question entries (overrides / additions / tombstones / history)
 * keyed by `id`. Untouched questions read straight from the job, so job
 * edits propagate to all candidates that haven't customised that question.
 *
 * Merge rules (in order):
 *   1. For each job question Q in the job's order:
 *      - If a candidate entry with `id === Q.id` exists and is `hidden`, skip.
 *      - Else if a candidate entry exists, use the candidate entry (it carries
 *        override text + sent/response history).
 *      - Else use the job's Q (live link to the job).
 *   2. Append any remaining candidate entries whose id is not present on the
 *      job. These fall into two cases:
 *      - **Addition**: a recruiter-added question with no `sentTime`/`response`
 *        — keep it (the candidate has questions the job doesn't).
 *      - **Orphan history**: a sent or answered entry whose job question has
 *        since been deleted — drop it. We don't surface answers for questions
 *        the recruiter has removed from the job.
 *      Hidden entries with no job counterpart are no-ops and skipped.
 */
export function getEffectiveQualificationQuestions(application, job) {
    const overrides = new Map((application?.qualificationQuestions ?? []).map(q => [q.id, q]));
    const result = [];
    for (const jq of job?.qualificationQuestions ?? []) {
        const ov = overrides.get(jq.id);
        if (ov !== undefined) {
            overrides.delete(jq.id);
            if (ov.hidden)
                continue;
            result.push(ov);
        }
        else {
            result.push(jq);
        }
    }
    for (const ov of overrides.values()) {
        if (ov.hidden)
            continue;
        // Drop orphan history: an answer/sent record whose job question has
        // been removed. Recruiter-added candidate-only questions (no history
        // yet) are preserved.
        if (ov.sentTimeUtc !== undefined || ov.response !== undefined)
            continue;
        result.push(ov);
    }
    return result.map(question => {
        if (!question.verifiedFromResume || question.evidence || question.answeredOnForm)
            return question;
        const unanswered = { ...question };
        delete unanswered.response;
        delete unanswered.responseTimeUtc;
        delete unanswered.verifiedFromResume;
        delete unanswered.reasoning;
        delete unanswered.scoreAfter;
        return unanswered;
    });
}
/**
 * Whether an application still has at least one qualification question that can
 * be sent (not yet sent, not yet answered). Used to decide whether to offer a
 * “screen” action. Mirrors the server-side guard in
 * ChatDomainService.loadSendContext so the client never offers a send that the
 * backend would reject.
 */
export function hasSendableQualificationQuestions(application, job) {
    return getEffectiveQualificationQuestions(application, job).some((q) => !q.sentTimeUtc && !q.response);
}
/**
 * The effective question awaiting an answer (sent, not yet answered), or null.
 * Effective (job) order is the single source of truth for "which question the
 * candidate is answering", so capture / record / score never diverge.
 */
export function findPendingQualificationQuestion(application, job) {
    return getEffectiveQualificationQuestions(application, job)
        .find((q) => q.sentTimeUtc && !q.response) ?? null;
}
/** The next effective question to send (not yet sent, not yet answered), or null. */
export function findNextQualificationQuestion(application, job) {
    return getEffectiveQualificationQuestions(application, job)
        .find((q) => !q.sentTimeUtc && !q.response) ?? null;
}
// Helper functions for working with the new candidate data model
export class CandidateJobUtils {
    /**
     * Get job-specific data for a candidate
     */
    static getJobApplication(candidate, jobId) {
        return candidate.applications?.[jobId];
    }
    /**
     * Check if candidate is associated with a specific job
     */
    static isAssociatedWithJob(candidate, jobId) {
        return !!candidate.applications?.[jobId];
    }
    /**
     * Get all job IDs that a candidate is associated with
     */
    static getAssociatedJobIds(candidate) {
        return Object.keys(candidate.applications || {});
    }
    /**
     * Get candidate's score for a specific job
     */
    static getJobScore(candidate, jobId) {
        return candidate.applications?.[jobId]?.score;
    }
    /**
     * Check if candidate has been scored for a specific job
     */
    static isScored(candidate, jobId) {
        const score = candidate.applications?.[jobId]?.score;
        return score !== undefined && score > 0;
    }
    /**
     * Add a candidate to a job (creates job-specific data)
     */
    static addToJob(candidate, jobId, jobData) {
        const defaultJobData = {
            jobId,
            reason: '',
            initialScore: 0,
            score: 0,
            state: CandidateState.Initialized,
            stageId: 'applied',
            stateTransitions: [],
            qualificationQuestions: [],
            questionsSent: false,
            ...jobData
        };
        return {
            ...candidate,
            applications: {
                ...candidate.applications,
                [jobId]: defaultJobData
            }
        };
    }
    /**
     * Update job-specific data for a candidate
     */
    static updateJobApplication(candidate, jobId, updates) {
        const existingJobData = candidate.applications?.[jobId];
        if (!existingJobData) {
            throw new Error(`Candidate ${candidate.id} is not associated with job ${jobId}`);
        }
        return {
            ...candidate,
            applications: {
                ...candidate.applications,
                [jobId]: {
                    ...existingJobData,
                    ...updates
                }
            }
        };
    }
    /**
     * Remove candidate from a job
     */
    static removeApplicationFromJob(candidate, jobId) {
        const newApplications = { ...candidate.applications };
        delete newApplications[jobId];
        return {
            ...candidate,
            applications: newApplications
        };
    }
    /**
     * Sort candidates by specified field and order
     */
    static sortCandidates(candidates, sortField, sortOrder) {
        return [...candidates].sort((a, b) => {
            let comparison = 0;
            switch (sortField) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'score': {
                    const aJobIds = CandidateJobUtils.getAssociatedJobIds(a);
                    const bJobIds = CandidateJobUtils.getAssociatedJobIds(b);
                    const aScore = aJobIds.length > 0
                        ? CandidateJobUtils.getJobScore(a, aJobIds[0]) || 0 : 0;
                    const bScore = bJobIds.length > 0
                        ? CandidateJobUtils.getJobScore(b, bJobIds[0]) || 0 : 0;
                    comparison = aScore - bScore;
                    break;
                }
                case 'addedTime':
                    comparison = a.addedTimeUtc - b.addedTimeUtc;
                    break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }
}
export var InterviewType;
(function (InterviewType) {
    InterviewType["Initial"] = "initial";
    InterviewType["Technical"] = "technical";
    InterviewType["Cultural"] = "cultural";
    InterviewType["Final"] = "final";
})(InterviewType || (InterviewType = {}));
/** Bumped when `InterviewInvitation.status` is written as the authority (§12.7). */
export const INTERVIEW_INVITATION_SCHEMA_VERSION = 1;
/**
 * Resolve the authoritative scheduling status: prefer `status`, else derive from
 * the legacy booleans for un-migrated docs (§12.7). Cancellation and decline take
 * precedence over confirmation.
 */
export function deriveInterviewStatus(inv) {
    if (inv.status !== undefined)
        return inv.status;
    if (inv.canceled === true)
        return 'canceled';
    if (inv.declined === true)
        return 'declined';
    if (inv.confirmed === true)
        return 'confirmed';
    return 'sent';
}
export function invIsConfirmed(inv) {
    return deriveInterviewStatus(inv) === 'confirmed';
}
export function invIsDeclined(inv) {
    return deriveInterviewStatus(inv) === 'declined';
}
export function invIsCanceled(inv) {
    return deriveInterviewStatus(inv) === 'canceled';
}
/**
 * Write `status` (+ schemaVersion) as the authority. Callers keep setting the
 * legacy boolean at their existing site; this deliberately does not touch the
 * orthogonal booleans (a cancel can follow a confirm).
 */
export function markInterviewStatus(inv, status) {
    inv.status = status;
    inv.schemaVersion = INTERVIEW_INVITATION_SCHEMA_VERSION;
}
/** Statuses in which the interview may still take place. */
const OPEN_INTERVIEW_STATUSES = new Set([
    'sent', 'negotiating', 'confirmed', 'handed_off',
]);
/** Whether the interview may still take place: not completed, declined or canceled. */
export function invIsOpen(inv) {
    return OPEN_INTERVIEW_STATUSES.has(deriveInterviewStatus(inv));
}
/** Hiring statuses that record the candidate came to the interview. */
const ATTENDED_HIRING_STATUSES = new Set([
    HiringStatus.InterviewAttended,
    HiringStatus.InterviewPassed,
    HiringStatus.InterviewFailed,
]);
/** Candidate states in which the interview has taken place. */
const INTERVIEW_DONE_STATES = new Set([
    CandidateState.InterviewCompleted,
    CandidateState.PostInterviewThanked,
]);
/**
 * When the candidate was checked in for the current interview (the first attendance recorded since it was sent),
 * or null. An earlier round's check-in doesn't count.
 */
export function interviewAttendedAtUtc(application) {
    const since = application.interviewInvitation?.sentTimeUtc ?? 0;
    const attended = (application.hiringStatusHistory ?? [])
        .filter((update) => ATTENDED_HIRING_STATUSES.has(update.status))
        .map(hiringStatusUpdateTimeUtc)
        .concat((application.stateTransitions ?? [])
        .filter((transition) => INTERVIEW_DONE_STATES.has(transition.state))
        .map((transition) => transition.timestampUtc))
        .filter((at) => at >= since);
    return attended.length > 0 ? toUtcMs(Math.min(...attended)) : null;
}
/** Candidate states past the interview: it's done, or an offer or a rejection followed. */
const PAST_INTERVIEW_STATES = new Set([
    CandidateState.InterviewCompleted,
    CandidateState.PostInterviewThanked,
    CandidateState.OfferReady,
    CandidateState.DecisionSent,
    CandidateState.DecisionResolved,
    CandidateState.Rejected,
]);
/** Whether a hiring status alone puts the candidate past the interview: it was judged, an offer is out or settled, or
 *  it ended (rejected, no-show, withdrew, hired). */
export function isPastInterviewHiringStatus(status) {
    return status === HiringStatus.InterviewPassed
        || status === HiringStatus.InterviewFailed
        || HIRING_STATUS_CONFIG[status]?.category === HiringStatusCategory.OfferFinal
        || isTerminalHiringStatus(status);
}
/**
 * Whether the candidate has moved past the interview: it's done or decided, an offer is under way or settled, or
 * they were rejected, didn't show, or withdrew. Checked in alone isn't past it (the decision is still to come).
 */
export function isPastInterview(application) {
    if (PAST_INTERVIEW_STATES.has(application.state))
        return true;
    const status = application.hiringStatus;
    return status !== undefined && isPastInterviewHiringStatus(status);
}
/**
 * When the candidate moved past the current interview: the first transition or hiring status since it was sent
 * that `isPastInterview` counts, or null.
 */
export function interviewPassedAtUtc(application) {
    const since = application.interviewInvitation?.sentTimeUtc ?? 0;
    const passed = (application.stateTransitions ?? [])
        .filter((transition) => PAST_INTERVIEW_STATES.has(transition.state))
        .map((transition) => transition.timestampUtc)
        .concat((application.hiringStatusHistory ?? [])
        .filter((update) => isPastInterviewHiringStatus(update.status))
        .map(hiringStatusUpdateTimeUtc))
        .filter((at) => at >= since);
    return passed.length > 0 ? toUtcMs(Math.min(...passed)) : null;
}
/**
 * Close the application's interview as the candidate moves past it: `completed` when they were checked in for it,
 * otherwise `canceled` with `reason` (shown to the recruiter). Null when no interview is open. Mutates the
 * application.
 */
export function closeInterviewInvitation(application, reason, nowUtc) {
    const inv = application.interviewInvitation;
    if (!inv || !invIsOpen(inv))
        return null;
    const slot = inv.selectedSlot ?? null;
    if (interviewAttendedAtUtc(application) !== null) {
        inv.completedTimeUtc = nowUtc;
        markInterviewStatus(inv, 'completed');
        return { outcome: 'completed', slot };
    }
    inv.canceled = true;
    inv.canceledTimeUtc = nowUtc;
    inv.cancelReason = reason;
    markInterviewStatus(inv, 'canceled');
    return { outcome: 'canceled', slot };
}
/** Why an open interview closes as the candidate moves to `state` (shown to the recruiter); null within the interview. */
export function interviewCloseReasonForState(state) {
    switch (state) {
        case CandidateState.InterviewSchedulingSent:
        case CandidateState.InterviewScheduled:
            return null;
        case CandidateState.InterviewCompleted:
        case CandidateState.PostInterviewThanked:
            return 'Interview completed';
        case CandidateState.OfferReady:
            return 'Moved to offer';
        // A decision sent from the decision dialog may be a rejection: only OfferReady is a move to the offer.
        case CandidateState.DecisionSent:
        case CandidateState.DecisionResolved:
            return 'Decision sent';
        case CandidateState.Rejected:
            return 'Rejected';
        case CandidateState.Processing:
        case CandidateState.Initialized:
        case CandidateState.ResumeScored:
        case CandidateState.QualificationQuestionSent:
        case CandidateState.QualificationAnswerReceived:
        case CandidateState.QualificationScored:
            return 'Moved back before the interview';
    }
}
/** Why an open interview closes as the hiring status becomes `status` (shown to the recruiter). */
export function interviewCloseReasonForStatus(status) {
    switch (status) {
        case HiringStatus.JobOffered: return 'Offer sent';
        case HiringStatus.OfferAccepted: return 'Offer accepted';
        case HiringStatus.OfferRejected: return 'Offer declined';
        case HiringStatus.Joined: return 'Hired';
        case HiringStatus.NotJoined: return 'Didn\'t join';
        case HiringStatus.NotInterested: return 'Withdrew';
        case HiringStatus.CandidateNoShow: return 'No-show';
        case HiringStatus.InterviewDeclined: return 'Declined the interview';
        case HiringStatus.InterviewPassed: return 'Interview passed';
        case HiringStatus.InterviewFailed: return 'Interview failed';
        default:
            return HIRING_STATUS_CONFIG[status]?.category === HiringStatusCategory.Rejection
                ? 'Rejected'
                : HIRING_STATUS_CONFIG[status]?.label ?? status;
    }
}
/** Candidate states at or past a booked interview — a late confirm reply then is stale. */
const POST_CONFIRM_STATES = new Set([
    CandidateState.InterviewScheduled,
    CandidateState.InterviewCompleted,
    CandidateState.PostInterviewThanked,
    CandidateState.DecisionSent,
    CandidateState.DecisionResolved,
    CandidateState.OfferReady,
    CandidateState.Rejected,
]);
/**
 * Whether a candidate reply can still confirm the interview, or is a STALE reply
 * that must be recorded as chat only (proposal §6.2). False once the invitation is
 * no longer awaiting a pick (confirmed / declined / canceled / handed_off), the
 * candidate has progressed to or past a booked interview, or reached a terminal
 * hiring status — so a late "yes, slot 1" can't book a calendar slot or drag the
 * candidate backward after the recruiter has moved them on.
 */
export function canConfirmInterview(application) {
    const inv = application.interviewInvitation;
    if (!inv?.sent)
        return false;
    const status = deriveInterviewStatus(inv);
    if (status !== 'sent' && status !== 'negotiating')
        return false;
    if (POST_CONFIRM_STATES.has(application.state))
        return false;
    if (application.hiringStatus !== undefined && isTerminalHiringStatus(application.hiringStatus))
        return false;
    return true;
}
/** Reviewed default policy; product may make these configurable later. */
export const DEFAULT_INTERVIEW_VIEW_POLICY = {
    noResponseAfterMinutes: 24 * 60,
    startsSoonBeforeMinutes: 30,
    overdueAfterMinutes: 15,
};
/** True when a derived state should appear under the "Needs attention" filter. */
export function interviewNeedsAttention(state) {
    return state.attentionReason !== null;
}
/**
 * Derive the interview row status for one application, at instant `atUtc`, under
 * `policy`. Pure and timezone-independent: all timing is absolute `UtcMs`. The
 * caller schedules a single refresh at `nextChangeAtUtc` so a confirmed row can
 * become "starts soon" without an unrelated event. Terminal-hiring-status
 * exclusion from the active worklist stays a caller/view concern.
 */
export function normalizeInterviewState(application, atUtc, policy) {
    const invitation = application.interviewInvitation ?? null;
    const invitationStatus = invitation ? deriveInterviewStatus(invitation) : null;
    const selectedSlot = invitation?.selectedSlot ?? null;
    const issues = [];
    const make = (lifecycle, attentionReason, nextChangeAtUtc) => ({ lifecycle, invitationStatus, attentionReason, selectedSlot, nextChangeAtUtc, issues });
    // 1) Completed — CandidateState is the authority, or the interview closed as completed.
    if (application.state === CandidateState.InterviewCompleted
        || application.state === CandidateState.PostInterviewThanked
        || invitationStatus === 'completed') {
        return make('completed', null, null);
    }
    // 2) Checked in — attendance recorded, awaiting completion.
    if (application.hiringStatus === HiringStatus.InterviewAttended) {
        return make('checked_in', 'awaiting_completion', null);
    }
    // 3) Confirmed appointment — timing decides attention.
    if (invitationStatus === 'confirmed') {
        if (!selectedSlot) {
            issues.push('confirmed_without_slot');
            return make('scheduling', 'invalid_data', null);
        }
        const start = selectedSlot.startTimeUtc;
        const soonAt = toUtcMs(start - policy.startsSoonBeforeMinutes * 60000);
        const overdueAt = toUtcMs(start + policy.overdueAfterMinutes * 60000);
        if (atUtc >= overdueAt)
            return make('scheduled', 'overdue', null);
        if (atUtc >= soonAt)
            return make('scheduled', 'starts_soon', overdueAt);
        return make('scheduled', null, soonAt);
    }
    // 4) Declined / canceled / handed off (invitation-only) — needs recruiter action.
    if (invitationStatus === 'declined')
        return make('scheduling', 'declined', null);
    if (invitationStatus === 'canceled')
        return make('scheduling', 'canceled', null);
    if (invitationStatus === 'handed_off')
        return make('scheduling', 'handed_off', null);
    // 5) Sent / negotiating — awaiting candidate; attention after the no-response window.
    if (invitationStatus === 'sent' || invitationStatus === 'negotiating') {
        if (selectedSlot)
            issues.push('slot_without_confirmation');
        const sentAt = invitation?.sentTimeUtc ?? null;
        if (sentAt !== null) {
            const nagAt = toUtcMs(sentAt + policy.noResponseAfterMinutes * 60000);
            if (atUtc >= nagAt)
                return make('scheduling', 'no_response', null);
            return make('scheduling', null, nagAt);
        }
        return make('scheduling', null, null);
    }
    // 6) No active invitation.
    return make('not_started', null, null);
}
/** Non-expired holders as of `nowUtc`. */
export function liveHolders(reservation, nowUtc) {
    return reservation.holders.filter((h) => h.expiresAtUtc > nowUtc);
}
/** The latest expiry among holders (null when none) — stored on the doc to drive the cleanup sweep. */
export function maxHolderExpiry(holders) {
    if (holders.length === 0)
        return null;
    return toUtcMs(Math.max(...holders.map((h) => h.expiresAtUtc)));
}
/** A slot is offerable to a new candidate iff it isn't booked and has room for another live hold. */
export function isSlotOfferable(reservation, maxHolds, nowUtc) {
    if (!reservation)
        return true;
    if (reservation.bookedBy)
        return false;
    return liveHolders(reservation, nowUtc).length < maxHolds;
}
/** Composite reservation document id — the slot uniqueness constraint (§12.4). */
export function reservationDocId(recruiterId, startUtc, endUtc) {
    return `${recruiterId}_${startUtc}_${endUtc}`;
}
/** Deterministic outbox id — stable across retries so re-enqueue is a no-op. */
export function outboxMessageId(turnKey, ordinal) {
    return `${turnKey}#${ordinal}`;
}
function deriveScreeningSubstatus(application) {
    const questions = application.qualificationQuestions ?? [];
    // A per-question score (scoreAfter) is the clean signal that screening finished.
    if (questions.some((q) => q.response != null && q.scoreAfter !== undefined))
        return 'scored';
    if (questions.some((q) => q.response != null))
        return 'answered';
    if (application.questionsSent || questions.some((q) => q.sentTimeUtc !== undefined))
        return 'awaiting_answers';
    return 'not_started';
}
function deriveInterviewSubstatus(application) {
    if (application.hiringStatus === HiringStatus.InterviewAttended)
        return 'attended';
    const inv = application.interviewInvitation;
    if (!inv)
        return 'sent';
    return deriveInterviewStatus(inv);
}
function deriveOfferSubstatus(application) {
    const offer = application.offerDecision;
    if (!offer?.sent)
        return 'pending';
    if (offer.accepted)
        return 'accepted';
    if (offer.rejected)
        return 'rejected';
    return 'sent';
}
/**
 * Resolve the scoped substatus for a candidate at their current `stageId`. The
 * stage's capability (SMS screening / interview / offer) selects the status
 * vocabulary; a manual or non-capability stage carries no sub-status.
 */
export function deriveSubstage(application, template) {
    const stage = template.stages.find((s) => s.id === application.stageId);
    const capability = stage && stage.kind === 'automated' ? stage.capability : null;
    switch (capability) {
        case StageCapability.SmsScreening:
            return { kind: 'screening', status: deriveScreeningSubstatus(application) };
        case StageCapability.Interview:
            return { kind: 'interview', status: deriveInterviewSubstatus(application) };
        case StageCapability.Offer:
            return { kind: 'offer', status: deriveOfferSubstatus(application) };
        default:
            return { kind: 'none' };
    }
}
export var CalendarEventType;
(function (CalendarEventType) {
    CalendarEventType["Interview"] = "Interview";
    CalendarEventType["PhoneScreen"] = "PhoneScreen";
    CalendarEventType["FollowUp"] = "FollowUp";
    CalendarEventType["Other"] = "Other";
})(CalendarEventType || (CalendarEventType = {}));
export var CalendarEventStatus;
(function (CalendarEventStatus) {
    CalendarEventStatus["Scheduled"] = "Scheduled";
    CalendarEventStatus["Confirmed"] = "Confirmed";
    CalendarEventStatus["Completed"] = "Completed";
    CalendarEventStatus["Canceled"] = "Canceled";
    CalendarEventStatus["Rescheduled"] = "Rescheduled";
})(CalendarEventStatus || (CalendarEventStatus = {}));
/** Whether an event is still on: neither canceled nor completed. */
export function isOpenCalendarEvent(event) {
    return event.status !== CalendarEventStatus.Canceled && event.status !== CalendarEventStatus.Completed;
}
// Settings
// Admin API
export var UserRole;
(function (UserRole) {
    UserRole["Admin"] = "admin";
    UserRole["Candidate"] = "candidate";
    UserRole["Recruiter"] = "recruiter";
    UserRole["Agent"] = "agent";
})(UserRole || (UserRole = {}));
// Industry context from landing pages - determines job templates shown
export var IndustryContext;
(function (IndustryContext) {
    IndustryContext["Logistics"] = "logistics";
    IndustryContext["Retail"] = "retail";
    IndustryContext["Hospitality"] = "hospitality";
    IndustryContext["Delivery"] = "delivery";
    IndustryContext["Ecommerce"] = "ecommerce";
    IndustryContext["Services"] = "services";
    IndustryContext["General"] = "general";
})(IndustryContext || (IndustryContext = {}));
// User screening role - for analytics and personalization
export var ScreeningRole;
(function (ScreeningRole) {
    ScreeningRole["Owner"] = "owner";
    ScreeningRole["Manager"] = "manager";
    ScreeningRole["Recruiter"] = "recruiter";
    ScreeningRole["Other"] = "other";
})(ScreeningRole || (ScreeningRole = {}));
/** Number of styles in the tag palette. MUST match TAG_STYLES.length in viewModelConfig.ts */
export const TAG_PALETTE_SIZE = 10;
// ============================================
// UNIFIED SETTINGS SYSTEM
// ============================================
// User Settings (per-user preferences)
export var RecruiterMcpMode;
(function (RecruiterMcpMode) {
    RecruiterMcpMode["Off"] = "off";
    RecruiterMcpMode["ReadOnly"] = "readOnly";
    RecruiterMcpMode["ApprovedActions"] = "approvedActions";
    RecruiterMcpMode["Autopilot"] = "autopilot";
})(RecruiterMcpMode || (RecruiterMcpMode = {}));
;
;
;
// Working Hours and Scheduling Models
export var DayOfWeek;
(function (DayOfWeek) {
    DayOfWeek[DayOfWeek["Sunday"] = 0] = "Sunday";
    DayOfWeek[DayOfWeek["Monday"] = 1] = "Monday";
    DayOfWeek[DayOfWeek["Tuesday"] = 2] = "Tuesday";
    DayOfWeek[DayOfWeek["Wednesday"] = 3] = "Wednesday";
    DayOfWeek[DayOfWeek["Thursday"] = 4] = "Thursday";
    DayOfWeek[DayOfWeek["Friday"] = 5] = "Friday";
    DayOfWeek[DayOfWeek["Saturday"] = 6] = "Saturday";
})(DayOfWeek || (DayOfWeek = {}));
/** Parse an <input type="time"> "HH:MM" value to minutes-since-local-midnight.
 *  The ONE place "HH:MM" is parsed — TimeRange stays numeric everywhere else. */
export function hhmmToMin(hhmm) {
    const [h, m] = hhmm.split(':');
    return Number(h) * 60 + Number(m);
}
/** Format minutes-since-local-midnight back to "HH:MM" for an <input type="time"> value. */
export function minToHhmm(min) {
    return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}
export const DEFAULT_APPLICATION_ACKNOWLEDGEMENT_TEMPLATE = 'Hi {{firstName}}, this is {{recruiterName}} from {{company}}. ' +
    'Thanks for applying for our {{jobTitle}} role. We received your application ' +
    'and will keep you updated here. Reply STOP to opt out.';
// ─── Anti-ghosting / follow-up sequence ───────────────────────────────────────
/** Lifecycle states for a scheduled follow-up message. */
export var ScheduledMessageStatus;
(function (ScheduledMessageStatus) {
    ScheduledMessageStatus["Pending"] = "Pending";
    ScheduledMessageStatus["Sent"] = "Sent";
    ScheduledMessageStatus["Delivered"] = "Delivered";
    ScheduledMessageStatus["Failed"] = "Failed";
    ScheduledMessageStatus["Cancelled"] = "Cancelled";
    ScheduledMessageStatus["Skipped"] = "Skipped";
})(ScheduledMessageStatus || (ScheduledMessageStatus = {}));
/** What event a touchpoint's offset is measured from. */
export var TouchpointAnchor;
(function (TouchpointAnchor) {
    /** Sequence start: the moment the candidate confirmed an interview slot. */
    TouchpointAnchor["SlotConfirmed"] = "SlotConfirmed";
    /** Relative to the confirmed interview's start time (`selectedSlot.startTime`). */
    TouchpointAnchor["InterviewStart"] = "InterviewStart";
    /** Relative to the confirmed interview's end time (`selectedSlot.endTime`). */
    TouchpointAnchor["InterviewEnd"] = "InterviewEnd";
    /** Fires on demand when the recruiter triggers a decision (no offset math). */
    TouchpointAnchor["RecruiterDecision"] = "RecruiterDecision";
})(TouchpointAnchor || (TouchpointAnchor = {}));
/** Seeded touchpoint ids — used for lazy-migration "still the seeded default" detection. */
export const SEEDED_TOUCHPOINT_IDS = {
    Confirmation: 'confirmation',
    DayBeforeReminder: 'day_before_reminder',
    DayOfReminder: 'day_of_reminder',
    FinalReminder: 'final_reminder',
    PostInterviewThanks: 'post_interview_thanks',
    DecisionOffer: 'decision_offer',
    DecisionRejection: 'decision_rejection',
};
// ==================== PENDING SESSION TYPES (Value-First Onboarding) ====================
/**
 * Status of a pending session for value-first onboarding
 */
export var PendingSessionStatus;
(function (PendingSessionStatus) {
    PendingSessionStatus["Pending"] = "pending";
    PendingSessionStatus["Converted"] = "converted";
    PendingSessionStatus["Expired"] = "expired";
})(PendingSessionStatus || (PendingSessionStatus = {}));
export const PUBLIC_APPLICATION_SMS_DISCLOSURE_VERSION = '2026-10-16';
/** Version of the WhatsApp/email opt-in wording on the public application form. */
export const PUBLIC_APPLICATION_CHANNEL_DISCLOSURE_VERSION = '2026-10-20';
/** Bucket upper bounds in ms. Anything ??? last bound goes to overflow bucket. */
export const TELEMETRY_BUCKET_BOUNDS_MS = [
    100, 200, 400, 800, 1500, 2500, 4000, 6500,
    10000, 15000, 25000, 40000, 60000, 120000, 300000,
];
/**
 * Channels an activity item can be delivered over (§7.1). Superset of
 * `MessageTransport` (which is candidate-facing message delivery) — adds `Web`
 * (the in-app inbox), which is always on.
 */
export var ActivityChannel;
(function (ActivityChannel) {
    ActivityChannel["Web"] = "web";
    ActivityChannel["WhatsApp"] = "whatsapp";
    ActivityChannel["Sms"] = "sms";
    ActivityChannel["Email"] = "email";
})(ActivityChannel || (ActivityChannel = {}));
/** Stable grouping users toggle per-channel in preferences (§3.2). */
export var ActivityCategory;
(function (ActivityCategory) {
    ActivityCategory["Applicants"] = "applicants";
    ActivityCategory["Screening"] = "screening";
    ActivityCategory["Interviews"] = "interviews";
    ActivityCategory["Offers"] = "offers";
    ActivityCategory["Account"] = "account";
    ActivityCategory["System"] = "system";
})(ActivityCategory || (ActivityCategory = {}));
/** The trigger types the pipeline emits, grouped by category (§4.1). */
export var ActivityType;
(function (ActivityType) {
    // Applicants
    ActivityType["ApplicantNew"] = "applicant.new";
    ActivityType["ApplicantScoredStrong"] = "applicant.scored_strong";
    ActivityType["ApplicantsBatchScored"] = "applicants.batch_scored";
    ActivityType["TalentPoolMatches"] = "talentpool.matches_found";
    // Screening
    ActivityType["ScreeningPassed"] = "screening.passed";
    ActivityType["ScreeningFailed"] = "screening.failed";
    ActivityType["CandidateRepliedOffScript"] = "candidate.replied_off_script";
    ActivityType["CandidateOptedOut"] = "candidate.opted_out";
    ActivityType["CandidateUnresponsive"] = "candidate.unresponsive";
    ActivityType["CandidateUnreachable"] = "candidate.unreachable";
    ActivityType["CandidateTextUndelivered"] = "candidate.text_undelivered";
    // Interviews
    ActivityType["InterviewConfirmed"] = "interview.confirmed";
    ActivityType["InterviewDeclined"] = "interview.declined";
    ActivityType["InterviewRescheduleRequested"] = "interview.reschedule_requested";
    ActivityType["InterviewToday"] = "interview.today";
    ActivityType["InterviewNoShowRisk"] = "interview.noshow_risk";
    ActivityType["InterviewNoShow"] = "interview.noshow";
    // Offers
    ActivityType["OfferAccepted"] = "offer.accepted";
    ActivityType["OfferRejected"] = "offer.rejected";
    ActivityType["OfferNegotiating"] = "offer.negotiating";
    // Account
    ActivityType["CreditsLow"] = "credits.low";
    ActivityType["CreditsExhausted"] = "credits.exhausted";
    ActivityType["PhoneVerificationNeeded"] = "phone.verification_needed";
    ActivityType["SubscriptionUpdated"] = "subscription.updated";
    // System
    ActivityType["CandidateScoringFailed"] = "candidate.scoring_failed";
    ActivityType["JobOptimizationDone"] = "job.optimization_completed";
    ActivityType["JobImportDone"] = "job.import_completed";
    ActivityType["SystemAlert"] = "system.alert";
})(ActivityType || (ActivityType = {}));
/** Maps each event type to the category users route/toggle it by (§3.2, §4.2). */
export const ACTIVITY_CATEGORY = {
    [ActivityType.ApplicantNew]: ActivityCategory.Applicants,
    [ActivityType.ApplicantScoredStrong]: ActivityCategory.Applicants,
    [ActivityType.ApplicantsBatchScored]: ActivityCategory.Applicants,
    [ActivityType.TalentPoolMatches]: ActivityCategory.Applicants,
    [ActivityType.ScreeningPassed]: ActivityCategory.Screening,
    [ActivityType.ScreeningFailed]: ActivityCategory.Screening,
    [ActivityType.CandidateRepliedOffScript]: ActivityCategory.Screening,
    [ActivityType.CandidateOptedOut]: ActivityCategory.Screening,
    [ActivityType.CandidateUnresponsive]: ActivityCategory.Screening,
    [ActivityType.CandidateUnreachable]: ActivityCategory.Screening,
    [ActivityType.CandidateTextUndelivered]: ActivityCategory.Screening,
    [ActivityType.InterviewConfirmed]: ActivityCategory.Interviews,
    [ActivityType.InterviewDeclined]: ActivityCategory.Interviews,
    [ActivityType.InterviewRescheduleRequested]: ActivityCategory.Interviews,
    [ActivityType.InterviewToday]: ActivityCategory.Interviews,
    [ActivityType.InterviewNoShowRisk]: ActivityCategory.Interviews,
    [ActivityType.InterviewNoShow]: ActivityCategory.Interviews,
    [ActivityType.OfferAccepted]: ActivityCategory.Offers,
    [ActivityType.OfferRejected]: ActivityCategory.Offers,
    [ActivityType.OfferNegotiating]: ActivityCategory.Offers,
    [ActivityType.CreditsLow]: ActivityCategory.Account,
    [ActivityType.CreditsExhausted]: ActivityCategory.Account,
    [ActivityType.PhoneVerificationNeeded]: ActivityCategory.Account,
    [ActivityType.SubscriptionUpdated]: ActivityCategory.Account,
    [ActivityType.CandidateScoringFailed]: ActivityCategory.System,
    [ActivityType.JobOptimizationDone]: ActivityCategory.System,
    [ActivityType.JobImportDone]: ActivityCategory.System,
    [ActivityType.SystemAlert]: ActivityCategory.System,
};
/** Intents an activity CTA can dispatch, executed by userCommandService (§5.1). */
export var ActivityCommandIntent;
(function (ActivityCommandIntent) {
    ActivityCommandIntent["StartScreening"] = "start_screening";
    ActivityCommandIntent["InviteInterview"] = "invite_interview";
    ActivityCommandIntent["Reject"] = "reject";
    ActivityCommandIntent["MarkHired"] = "mark_hired";
    ActivityCommandIntent["MarkNoShow"] = "mark_noshow";
    ActivityCommandIntent["SendReminder"] = "send_reminder";
    ActivityCommandIntent["Retry"] = "retry";
})(ActivityCommandIntent || (ActivityCommandIntent = {}));
/**
 * Whether an event should reach a user over a channel. Default (nothing stored)
 * = on; a global channel switch OR a per-event cell set to false turns it off.
 * Channel availability (shipped/configured) is enforced separately per transport.
 */
export function isEventChannelEnabled(prefs, type, channel) {
    if (prefs?.channels?.[channel] === false)
        return false;
    return prefs?.matrix?.[type]?.[channel] ?? true;
}
